import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useUserData } from '../context/UserDataContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, AreaChart, Area,
} from 'recharts';
import {
  TrendingUp, TrendingDown, Minus, Pencil, Check, X, Info, Plus, Trash2,
  AlertTriangle, ArrowUpCircle, ArrowDownCircle, Zap, Bell, Calendar, Scissors, RefreshCw, Lock,
} from 'lucide-react';
import EmptyState from './EmptyState';
import { categorizeTransaction, KEYWORD_RULES } from '../lib/categorize';
import { fetchPlaidTransactions } from '../lib/plaidSync';

// ── Formatters ─────────────────────────────────────────────────────────────
const fmt  = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const fmtD = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

// ── Standard categories (single source of truth) ───────────────────────────
// All names here must exactly match what categorize.js KEYWORD_RULES outputs.
export const STANDARD_CATEGORIES = [
  // Needs
  { name: 'Housing',               icon: '🏠', type: 'Need',    group: 'Needs' },
  { name: 'Utilities',             icon: '💡', type: 'Need',    group: 'Needs' },
  { name: 'Groceries',             icon: '🛒', type: 'Need',    group: 'Needs' },
  { name: 'Transportation',        icon: '🚗', type: 'Need',    group: 'Needs' },
  { name: 'Insurance',             icon: '🛡️', type: 'Need',    group: 'Needs' },
  { name: 'Healthcare',            icon: '💊', type: 'Need',    group: 'Needs' },
  { name: 'Minimum Debt Payments', icon: '💳', type: 'Need',    group: 'Needs' },
  // Wants
  { name: 'Dining Out',            icon: '🍽️', type: 'Want',    group: 'Wants' },
  { name: 'Entertainment',         icon: '🎬', type: 'Want',    group: 'Wants' },
  { name: 'Shopping',              icon: '🛍️', type: 'Want',    group: 'Wants' },
  { name: 'Subscriptions',         icon: '📱', type: 'Want',    group: 'Wants' },
  { name: 'Personal Care',         icon: '💆', type: 'Want',    group: 'Wants' },
  { name: 'Travel',                icon: '✈️', type: 'Want',    group: 'Wants' },
  { name: 'Fitness',               icon: '💪', type: 'Want',    group: 'Wants' },
  { name: 'Hobbies',               icon: '🎮', type: 'Want',    group: 'Wants' },
  // Savings
  { name: 'Emergency Fund',        icon: '🆘', type: 'Savings', group: 'Savings' },
  { name: 'Retirement',            icon: '🎯', type: 'Savings', group: 'Savings' },
  { name: 'Investments',           icon: '📈', type: 'Savings', group: 'Savings' },
  { name: 'Extra Debt Paydown',    icon: '💸', type: 'Savings', group: 'Savings' },
  { name: 'Education Fund',        icon: '🎓', type: 'Savings', group: 'Savings' },
];

// The "Other" escape hatch — shown at bottom of picker
const OTHER_CATEGORY = { name: 'Other', icon: '🔖', type: 'Want', group: 'Other' };

// ALL_CATEGORIES includes standards + Other (used in review/transaction dropdowns)
const ALL_CATEGORIES = [...STANDARD_CATEGORIES, OTHER_CATEGORY];

// ── Benchmarks (% of income) ────────────────────────────────────────────────
const BENCHMARKS = {
  'Housing':               { pct: 28, range: '≤28% of income',  tip: 'Keep rent/mortgage under 28% of gross income. Include HOA and property tax.' },
  'Utilities':             { pct: 3,  range: '2–5% of income',  tip: 'Electric, water, internet, phone. A smart thermostat + LED swap saves $30–80/mo.' },
  'Groceries':             { pct: 7,  range: '5–10% of income', tip: 'Home cooking vs dining out can save $300–500/month. Meal prep reduces waste.' },
  'Transportation':        { pct: 10, range: '10–15% of income',tip: 'Car payment + gas + maintenance. Public transit or biking can cut this in half.' },
  'Insurance':             { pct: 4,  range: '3–5% of income',  tip: 'Bundle home + auto for discounts. Review coverage annually as life changes.' },
  'Healthcare':            { pct: 3,  range: '2–5% of income',  tip: 'Max your HSA for a triple tax advantage: deductible, grows, withdraws tax-free.' },
  'Minimum Debt Payments': { pct: 5,  range: '≤5% of income',  tip: 'Minimums keep you current. Any extra goes to Extra Debt Paydown to save on interest.' },
  'Dining Out':            { pct: 5,  range: '3–6% of income',  tip: 'The single biggest discretionary lever. Every meal at home saves $12–25 on average.' },
  'Entertainment':         { pct: 3,  range: '2–4% of income',  tip: 'Movies, concerts, games — your fun money. Track it without guilt, just consciously.' },
  'Shopping':              { pct: 4,  range: '3–5% of income',  tip: 'Clothing, electronics, household goods. Try a 30-day wishlist before buying.' },
  'Subscriptions':         { pct: 1,  range: '1–2% of income',  tip: 'Americans average $200+/month in forgotten subscriptions. Audit every quarter.' },
  'Personal Care':         { pct: 2,  range: '1–3% of income',  tip: 'Haircuts, salon, spa. Shop around — prices vary 3× for similar services.' },
  'Travel':                { pct: 5,  range: '3–7% of income',  tip: 'Save monthly rather than lumping it. Travel rewards cards can offset 30–50% of costs.' },
  'Fitness':               { pct: 2,  range: '1–3% of income',  tip: 'Community gyms and free apps can replicate $150/month gym results for $25/month.' },
  'Hobbies':               { pct: 2,  range: '1–3% of income',  tip: 'Hobbies reduce stress and increase well-being. Budget intentionally rather than ad hoc.' },
  'Emergency Fund':        { pct: 10, range: '10–15% of income',tip: 'Target 3–6 months of expenses. Keep it in a high-yield savings account (HYSA).' },
  'Retirement':            { pct: 10, range: '10–15% of income',tip: '401k to employer match → Roth IRA → HSA → back to 401k → taxable brokerage.' },
  'Investments':           { pct: 5,  range: '5–10% of income', tip: 'Low-cost index funds beat 90% of active managers over 20 years. Start simple.' },
  'Extra Debt Paydown':    { pct: 3,  range: '2–5% of income',  tip: 'Target highest-interest debt first (avalanche). Saves more than the minimum-only path.' },
  'Education Fund':        { pct: 2,  range: '1–3% of income',  tip: '529 plans grow tax-free for qualified education expenses. Start early for max compounding.' },
};

// ── Rule buckets (for 50/30/20) ─────────────────────────────────────────────
const RULE_BUCKETS = {
  // Needs
  'Housing': 'needs', 'Utilities': 'needs', 'Groceries': 'needs',
  'Transportation': 'needs', 'Insurance': 'needs', 'Healthcare': 'needs',
  'Minimum Debt Payments': 'needs',
  // Wants
  'Dining Out': 'wants', 'Entertainment': 'wants', 'Shopping': 'wants',
  'Subscriptions': 'wants', 'Personal Care': 'wants', 'Travel': 'wants',
  'Fitness': 'wants', 'Hobbies': 'wants',
  // Savings
  'Emergency Fund': 'savings', 'Retirement': 'savings', 'Investments': 'savings',
  'Extra Debt Paydown': 'savings', 'Education Fund': 'savings',
};

// Fallback: maps Plaid primary category codes → standard budget category + type
const PLAID_TO_BUDGET = {
  'FOOD_AND_DRINK':              { category: 'Dining Out',            type: 'Want' },
  'TRANSPORTATION':              { category: 'Transportation',        type: 'Need' },
  'GENERAL_MERCHANDISE':        { category: 'Shopping',              type: 'Want' },
  'MEDICAL':                    { category: 'Healthcare',            type: 'Need' },
  'ENTERTAINMENT':              { category: 'Entertainment',         type: 'Want' },
  'ENTERTAINMENT_SUBSCRIPTIONS': { category: 'Subscriptions',        type: 'Want' },
  'RENT_AND_UTILITIES':         { category: 'Housing',               type: 'Need' },
  'HOME_IMPROVEMENT':           { category: 'Housing',               type: 'Need' },
  'PERSONAL_CARE':              { category: 'Personal Care',         type: 'Want' },
  'TRAVEL':                     { category: 'Travel',                type: 'Want' },
  'EDUCATION':                  { category: 'Education Fund',        type: 'Savings'},
  'LOAN_PAYMENTS':              { category: 'Minimum Debt Payments', type: 'Need' },
  'GENERAL_SERVICES':           { category: 'Shopping',              type: 'Want' },
  'GOVERNMENT_AND_NON_PROFIT':  { category: 'Shopping',              type: 'Want' },
  'BANK_FEES':                  { category: 'Shopping',              type: 'Want' },
};

// Migration map: old category name → new standard name
// Used once on Budget mount to rename legacy categories.
const CATEGORY_MIGRATION = {
  'Food & Dining':   'Dining Out',
  'Savings':         'Emergency Fund',
  'Education':       'Education Fund',
  // identical names need no entry (Housing, Utilities, Transportation, etc.)
};

const TYPE_COLORS = {
  'Need':    { text: 'text-blue-400',    bg: 'bg-blue-900/30',    border: 'border-blue-700/40'    },
  'Want':    { text: 'text-purple-400',  bg: 'bg-purple-900/30',  border: 'border-purple-700/40'  },
  'Savings': { text: 'text-emerald-400', bg: 'bg-emerald-900/30', border: 'border-emerald-700/40' },
};

// ── Auto-Categorization Engine ──────────────────────────────────────────────
// (KEYWORD_RULES and categorizeTransaction imported from ../lib/categorize)

/**
 * Maps a standard category name (from KEYWORD_RULES) to the best-matching
 * user category.  When users create custom names like "food" instead of
 * "Dining Out", this bridges the gap so transactions count toward the right row.
 *
 * Priority:
 *  1. Exact name match
 *  2. Case-insensitive substring match (e.g. "travel" ↔ "Travel")
 *  3. Keyword proximity — if the user's category name appears inside any
 *     KEYWORD_RULES keyword for the standard category (e.g. "food" appears
 *     in "food truck", a Dining Out keyword → map to user's "food")
 *  4. Sole type match — only one user category of that Need/Want/Savings type
 *  5. No match — return the standard name unchanged
 */
// Maps new standard category names → closest legacy equivalent, in priority order.
// Activated only when the user doesn't yet have the preferred standard category.
// This ensures transactions always land under a category row the user can see.
const CATEGORY_FALLBACKS = {
  // New splits from "Food & Dining"
  'Groceries':             ['Dining Out', 'Food & Dining', 'Shopping'],
  'Dining Out':            ['Food & Dining', 'Shopping'],
  // Fitness/Hobbies were under Entertainment
  'Fitness':               ['Entertainment', 'Shopping'],
  'Hobbies':               ['Entertainment', 'Shopping'],
  // Insurance was often grouped with Transportation
  'Insurance':             ['Transportation', 'Healthcare'],
  // Debt/loan categories
  'Minimum Debt Payments': ['Housing', 'Transportation'],
  'Extra Debt Paydown':    ['Emergency Fund', 'Savings', 'Investments'],
  // Savings sub-categories
  'Emergency Fund':        ['Savings', 'Investments'],
  'Retirement':            ['Investments', 'Savings'],
  'Education Fund':        ['Investments', 'Savings'],
  // Personal Care was under Shopping
  'Personal Care':         ['Shopping', 'Healthcare'],
  // Travel was under Entertainment
  'Travel':                ['Entertainment', 'Shopping'],
};

function mapToUserCategory(standardCat, standardType, userCategories) {
  if (!userCategories.length) return standardCat;

  // 1. Exact match
  if (userCategories.some(c => c.name === standardCat)) return standardCat;

  // 2. Explicit fallback chain — try each preferred alias in order
  const fallbacks = CATEGORY_FALLBACKS[standardCat] || [];
  for (const alias of fallbacks) {
    const match = userCategories.find(c => c.name === alias);
    if (match) return match.name;
  }

  // 3. Case-insensitive substring match
  const stdLower = standardCat.toLowerCase();
  for (const c of userCategories) {
    const uLower = c.name.toLowerCase();
    if (stdLower.includes(uLower) || uLower.includes(stdLower)) return c.name;
  }

  // 4. Keyword proximity — find KEYWORD_RULES for this standard category,
  //    then check if any keyword contains the user category name as a whole word
  //    (word-boundary match prevents "car" matching inside "instacart").
  for (const c of userCategories) {
    const uLower = c.name.toLowerCase();
    if (uLower.length < 2) continue;
    const pattern = new RegExp(`\\b${uLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`);
    for (const rule of KEYWORD_RULES) {
      if (rule.category === standardCat && rule.keywords.some(kw => pattern.test(kw))) {
        return c.name;
      }
    }
  }

  // 5. Sole type match
  if (standardType) {
    const sameType = userCategories.filter(c => c.type === standardType);
    if (sameType.length === 1) return sameType[0].name;
  }

  return standardCat;
}

// ── Helpers ──────────────────────────────────────────────────────────────────
// Some data sources store expenses as kind:"actual" instead of kind:"expense".
// This helper normalises both so all filters work uniformly.
const isExpense = (t) => t.kind === 'expense' || t.kind === 'actual';

// ── Date helpers ────────────────────────────────────────────────────────────

// Returns today's date as a local YYYY-MM-DD string (not UTC, which can be off
// by a day in US timezones late at night or when the machine clock differs).
function localDateString(d = new Date()) {
  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const dd   = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// getDateRange — returns { start, end } Date objects for filtering.
//
// CRITICAL: end is set to 23:59:59.999 of today, NOT the current clock time.
// Transactions are stored as YYYY-MM-DD strings and compared at noon (T12:00)
// to avoid DST edge cases. If end were "now" (e.g. 9 AM), any transaction
// added before noon would have a noon timestamp AFTER periodEnd and would be
// silently filtered out — making the whole UI appear broken.
function getDateRange(period) {
  const now = new Date();
  // End of today in local time — always after any same-day transaction's noon timestamp
  const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

  if (period === 'week') {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay(), 0, 0, 0, 0);
    return { start, end: endOfToday };
  }
  if (period === 'last30') {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29, 0, 0, 0, 0);
    return { start, end: endOfToday };
  }
  if (period === 'month') {
    return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: endOfToday };
  }
  if (period === 'year') {
    return { start: new Date(now.getFullYear(), 0, 1), end: endOfToday };
  }
  return { start: new Date(0), end: endOfToday };
}

function makeTxnId() {
  return `txn_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

// Returns current month as 'YYYY-MM'
function currentMonthKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function makeBillId() {
  return `bill_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

// Returns a Date for the bill's due date in the given 'YYYY-MM' month.
// dueDay is clamped to the last real day of the month (handles Feb 28/29, etc.).
function billDueDateForMonth(bill, monthKey) {
  const [y, m] = monthKey.split('-').map(Number);
  const lastDay = new Date(y, m, 0).getDate();
  const day = Math.min(bill.dueDay, lastDay);
  return new Date(y, m - 1, day);
}

// ── AddTransactionModal ─────────────────────────────────────────────────────
function AddTransactionModal({ onClose, onAdd, onAddMultiple, userCategories, defaultKind = 'expense', customRules = {} }) {
  const [kind, setKind]               = useState(defaultKind);
  const [amount, setAmount]           = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate]               = useState(localDateString());
  const [category, setCategory]       = useState('');
  const [type, setType]               = useState('');
  const [autoCat, setAutoCat]         = useState(null);

  // ── Split mode state ──────────────────────────────────────────────────────
  const [splitMode, setSplitMode] = useState(false);
  const [splits, setSplits] = useState([
    { id: 1, category: '', type: '', amount: '' },
    { id: 2, category: '', type: '', amount: '' },
  ]);
  const splitIdRef = React.useRef(3);

  // Split math (derived inline)
  const totalAmt  = parseFloat(amount.replace(/[^0-9.]/g, '')) || 0;
  const splitSum  = splits.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const remaining = +(totalAmt - splitSum).toFixed(2);
  const splitsValid = splitMode
    ? Math.abs(remaining) < 0.01 && splits.length >= 2 &&
      splits.every(r => r.category && r.amount && parseFloat(r.amount) > 0)
    : true;

  const resetSplits = () => {
    setSplits([
      { id: 1, category: '', type: '', amount: '' },
      { id: 2, category: '', type: '', amount: '' },
    ]);
    splitIdRef.current = 3;
  };

  const handleDescChange = (val) => {
    setDescription(val);
    if (kind === 'expense' && !category && !splitMode) {
      setAutoCat(categorizeTransaction(val, customRules));
    }
  };

  const applyAutoCat = () => {
    if (autoCat) { setCategory(autoCat.category); setType(autoCat.type); setAutoCat(null); }
  };

  const handleCatChange = (val) => {
    setCategory(val);
    if (val && !type) {
      const info = ALL_CATEGORIES.find(c => c.name === val);
      if (info) setType(info.type);
    }
  };

  const updateSplitRow = (id, field, value) => {
    setSplits(prev => prev.map(r => {
      if (r.id !== id) return r;
      const updated = { ...r, [field]: value };
      // Auto-fill type when category changes
      if (field === 'category' && value && !r.type) {
        const info = ALL_CATEGORIES.find(c => c.name === value);
        if (info) updated.type = info.type;
      }
      return updated;
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const amt = parseFloat(amount.replace(/[^0-9.]/g, ''));
    if (isNaN(amt) || amt <= 0 || !description.trim()) return;

    // ── Split path ───────────────────────────────────────────────────────────
    if (splitMode && kind === 'expense') {
      if (Math.abs(remaining) >= 0.01) return;
      const splitTxns = splits.map(row => {
        const splitAmt = parseFloat(row.amount);
        const info     = ALL_CATEGORIES.find(c => c.name === row.category);
        const finalType = row.type || (info ? info.type : null);
        return {
          id:          makeTxnId(),
          date,
          amount:      splitAmt,
          description: description.trim(),
          category:    row.category || null,
          type:        finalType || null,
          kind:        'expense',
          source:      'manual',
          reviewed:    !!(row.category && finalType),
        };
      });
      if (onAddMultiple) onAddMultiple(splitTxns);
      onClose();
      return;
    }

    // ── Single-transaction path (unchanged) ──────────────────────────────────
    const result   = kind === 'expense' ? categorizeTransaction(description, customRules) : null;
    const finalCat = category || (result ? result.category : null);
    const finalType = type   || (result ? result.type    : null);
    onAdd({
      id: makeTxnId(),
      date,
      amount: amt,
      description: description.trim(),
      category:  finalCat  || null,
      type:      finalType || null,
      kind,
      source: category ? 'manual' : (result ? result.source : 'manual'),
      reviewed: !!(finalCat && finalType),
    });
    onClose();
  };

  const catNames = useMemo(() => {
    const names = new Set(ALL_CATEGORIES.map(c => c.name));
    userCategories.forEach(c => names.add(c.name));
    return Array.from(names);
  }, [userCategories]);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className={`bg-slate-800 border border-slate-700 rounded-2xl w-full p-6 max-h-[90vh] overflow-y-auto ${
          splitMode ? 'max-w-lg' : 'max-w-md'
        }`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-slate-100 text-lg">
            {splitMode ? '✂ Split Transaction' : kind === 'expense' ? '↓ Log Expense' : '↑ Log Income'}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors">
            <X size={16} className="text-slate-400" />
          </button>
        </div>

        {/* Kind toggle */}
        <div className="flex rounded-lg bg-slate-700/50 p-1 mb-4 gap-1">
          {[['expense', '↓ Expense'], ['income', '↑ Income']].map(([k, l]) => (
            <button
              key={k} type="button"
              onClick={() => { setKind(k); setAutoCat(null); setCategory(''); setType(''); if (splitMode) { setSplitMode(false); resetSplits(); } }}
              className={`flex-1 text-sm py-2 rounded-md font-medium transition-colors ${
                kind === k
                  ? k === 'expense' ? 'bg-rose-600 text-white' : 'bg-emerald-600 text-white'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >{l}</button>
          ))}
        </div>

        {/* Split toggle — expense only */}
        {kind === 'expense' && (
          <div className="flex justify-end mb-3">
            <button
              type="button"
              onClick={() => { setSplitMode(s => !s); resetSplits(); setAutoCat(null); }}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors ${
                splitMode
                  ? 'bg-violet-700/40 border-violet-600 text-violet-300'
                  : 'bg-slate-700 border-slate-600 text-slate-400 hover:text-slate-200 hover:border-slate-500'
              }`}
            >
              <Scissors size={11} />
              {splitMode ? 'Split On' : 'Split Transaction'}
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Amount + Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">
                {splitMode ? 'Total Amount' : 'Amount'}
              </label>
              <div className="flex items-center gap-1.5 bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 focus-within:border-blue-500 transition-colors">
                <span className="text-slate-400 text-sm">$</span>
                <input
                  type="number" value={amount} onChange={e => setAmount(e.target.value)}
                  placeholder="0.00" min="0" step="0.01" autoFocus required
                  className="flex-1 bg-transparent text-slate-100 text-sm focus:outline-none tabular-nums"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Date</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} required
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Description / Merchant</label>
            <input
              value={description} onChange={e => handleDescChange(e.target.value)}
              placeholder="e.g. Walmart, Netflix, Rent..." required
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
            />
            {autoCat && !category && kind === 'expense' && !splitMode && (
              <div className="mt-1.5 flex items-center gap-1.5 text-xs text-blue-400">
                <Zap size={10} />
                Auto-detected: <span className="font-medium">{autoCat.category}</span>
                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${TYPE_COLORS[autoCat.type]?.bg} ${TYPE_COLORS[autoCat.type]?.text}`}>
                  {autoCat.type}
                </span>
                <button type="button" onClick={applyAutoCat} className="underline hover:no-underline ml-1">Apply</button>
              </div>
            )}
          </div>

          {/* ── SPLIT MODE ── */}
          {kind === 'expense' && splitMode ? (
            <div className="space-y-3">
              {/* Live sum indicator */}
              <div className={`flex items-center justify-between text-xs px-3 py-2 rounded-lg border ${
                Math.abs(remaining) < 0.01
                  ? 'bg-emerald-900/30 border-emerald-700/30 text-emerald-400'
                  : 'bg-amber-900/30 border-amber-700/30 text-amber-400'
              }`}>
                <span>
                  {Math.abs(remaining) < 0.01
                    ? `✓ Splits add up to ${fmtD(totalAmt)}`
                    : remaining > 0
                      ? `${fmtD(remaining)} still unallocated`
                      : `${fmtD(Math.abs(remaining))} over total`}
                </span>
                {remaining > 0.005 && (
                  <button
                    type="button"
                    onClick={() => setSplits(prev => prev.map((r, i) =>
                      i === prev.length - 1 ? { ...r, amount: String(remaining.toFixed(2)) } : r
                    ))}
                    className="underline hover:no-underline font-medium ml-3 flex-shrink-0"
                  >
                    Fill last row
                  </button>
                )}
              </div>

              {/* Split rows */}
              <div className="space-y-2">
                {splits.map((row) => (
                  <div key={row.id}
                    className="flex items-end gap-2 p-3 bg-slate-700/30 rounded-xl border border-slate-600/40">
                    <div className="flex-1 min-w-0">
                      <label className="text-xs text-slate-400 mb-1 block">Category</label>
                      <select
                        value={row.category}
                        onChange={e => updateSplitRow(row.id, 'category', e.target.value)}
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-2.5 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
                      >
                        <option value="">— Select —</option>
                        {catNames.map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                    <div className="w-28 flex-shrink-0">
                      <label className="text-xs text-slate-400 mb-1 block">Amount</label>
                      <div className="flex items-center gap-1 bg-slate-700 border border-slate-600 rounded-lg px-2 py-2 focus-within:border-blue-500 transition-colors">
                        <span className="text-slate-400 text-xs">$</span>
                        <input
                          type="number"
                          value={row.amount}
                          onChange={e => updateSplitRow(row.id, 'amount', e.target.value)}
                          placeholder="0.00"
                          min="0.01"
                          step="0.01"
                          className="flex-1 bg-transparent text-slate-100 text-sm focus:outline-none tabular-nums w-full"
                        />
                      </div>
                    </div>
                    {splits.length > 2 && (
                      <button
                        type="button"
                        onClick={() => setSplits(prev => prev.filter(r => r.id !== row.id))}
                        className="p-1.5 text-slate-500 hover:text-rose-400 transition-colors flex-shrink-0 mb-0.5"
                        aria-label="Remove split"
                      >
                        <X size={13} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Add another split */}
              <button
                type="button"
                onClick={() => setSplits(prev => [...prev, { id: splitIdRef.current++, category: '', type: '', amount: '' }])}
                className="flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 transition-colors"
              >
                <Plus size={11} /> Add another split
              </button>
            </div>

          ) : kind === 'expense' ? (
            /* ── NORMAL EXPENSE: category + type ── */
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400 mb-1.5 block">
                  Category <span className="text-slate-600">(optional)</span>
                </label>
                <select value={category} onChange={e => handleCatChange(e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500">
                  <option value="">— Auto-detect —</option>
                  {catNames.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1.5 block">
                  Type <span className="text-slate-600">(optional)</span>
                </label>
                <select value={type} onChange={e => setType(e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500">
                  <option value="">— Auto-detect —</option>
                  <option value="Need">Need</option>
                  <option value="Want">Want</option>
                  <option value="Savings">Savings</option>
                </select>
              </div>
            </div>
          ) : null}

          {/* Submit + Cancel */}
          <div className="flex gap-3 pt-1">
            <button
              type="submit"
              disabled={splitMode && !splitsValid}
              className={`flex-1 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors
                disabled:opacity-40 disabled:cursor-not-allowed ${
                kind === 'expense' ? 'bg-rose-600 hover:bg-rose-500 disabled:hover:bg-rose-600'
                                   : 'bg-emerald-600 hover:bg-emerald-500'
              }`}
            >
              {splitMode
                ? `Log ${splits.length} Splits`
                : kind === 'expense' ? 'Log Expense' : 'Log Income'}
            </button>
            <button type="button" onClick={onClose}
              className="px-4 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm py-2.5 rounded-lg transition-colors">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── ReviewTransactionModal ──────────────────────────────────────────────────
function ReviewTransactionModal({ transaction, userCategories, onSave, onClose }) {
  const [category, setCategory] = useState(transaction.category || '');
  const [type,     setType]     = useState(transaction.type || '');
  const [remember, setRemember] = useState(true);

  const merchantKey = transaction.description.toLowerCase().trim().split(/\s+/).slice(0, 3).join(' ');

  const handleSave = () => {
    if (!category || !type) return;
    onSave({ category, type, remember });
    onClose();
  };

  const handleSelectCategory = (cat) => {
    setCategory(cat.name);
    setType(cat.type);  // always override type when category selected
  };

  // Build merged list: standard categories + any user-only categories not in standard list
  const standardNames = new Set(STANDARD_CATEGORIES.map(c => c.name));
  const extraUserCats = userCategories.filter(c => !standardNames.has(c.name));

  // Group standard categories by group
  const groups = ['Needs', 'Wants', 'Savings'];
  const groupedCats = groups.map(g => ({
    label: g,
    cats: STANDARD_CATEGORIES.filter(c => c.group === g),
  }));

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-100 text-lg">Categorize Transaction</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors">
            <X size={16} className="text-slate-400" />
          </button>
        </div>

        {/* Transaction card */}
        <div className="p-3 bg-slate-700/50 rounded-xl mb-4">
          <div className="text-sm font-semibold text-slate-200 mb-1">{transaction.description}</div>
          <div className="flex justify-between text-xs text-slate-400">
            <span>{new Date(transaction.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
            <span className="font-semibold text-rose-400">{fmtD(transaction.amount)}</span>
          </div>
        </div>

        {/* Category visual grid */}
        <div className="overflow-y-auto max-h-64 space-y-3 pr-1 mb-4">
          {groupedCats.map(({ label, cats }) => (
            <div key={label}>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">{label}</div>
              <div className="grid grid-cols-3 gap-1.5">
                {cats.map(cat => (
                  <button key={cat.name} type="button" onClick={() => handleSelectCategory(cat)}
                    className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors border text-left ${
                      category === cat.name
                        ? `${TYPE_COLORS[cat.type].bg} ${TYPE_COLORS[cat.type].text} ${TYPE_COLORS[cat.type].border}`
                        : 'bg-slate-700/40 text-slate-300 border-slate-600/50 hover:bg-slate-700 hover:text-slate-100'
                    }`}
                  >
                    <span>{cat.icon}</span>
                    <span className="truncate">{cat.name}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
          {extraUserCats.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Custom</div>
              <div className="grid grid-cols-3 gap-1.5">
                {extraUserCats.map(cat => (
                  <button key={cat.name} type="button" onClick={() => handleSelectCategory(cat)}
                    className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors border text-left ${
                      category === cat.name
                        ? `${TYPE_COLORS[cat.type]?.bg || 'bg-slate-600'} ${TYPE_COLORS[cat.type]?.text || 'text-slate-100'} ${TYPE_COLORS[cat.type]?.border || 'border-slate-500'}`
                        : 'bg-slate-700/40 text-slate-300 border-slate-600/50 hover:bg-slate-700 hover:text-slate-100'
                    }`}
                  >
                    <span>{cat.icon || '🔖'}</span>
                    <span className="truncate">{cat.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Budget Type override */}
        <div className="mb-4">
          <label className="text-xs text-slate-400 mb-1.5 block font-medium">Budget Type</label>
          <div className="grid grid-cols-3 gap-2">
            {[['Need', '🔵'], ['Want', '🟣'], ['Savings', '🟢']].map(([t, e]) => (
              <button key={t} type="button" onClick={() => setType(t)}
                className={`py-2 rounded-lg text-sm font-medium transition-colors border ${
                  type === t
                    ? `${TYPE_COLORS[t].bg} ${TYPE_COLORS[t].text} ${TYPE_COLORS[t].border}`
                    : 'bg-slate-700/40 text-slate-400 border-slate-600 hover:bg-slate-700'
                }`}
              >{e} {t}</button>
            ))}
          </div>
        </div>

        <label className="flex items-center gap-2.5 cursor-pointer group select-none mb-5">
          <div onClick={() => setRemember(r => !r)}
            className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
              remember ? 'bg-blue-600 border-blue-600' : 'border-slate-500 group-hover:border-slate-400'
            }`}
          >
            {remember && <Check size={10} className="text-white" />}
          </div>
          <span className="text-xs text-slate-300">
            Remember "<span className="font-medium text-slate-200">{merchantKey}</span>" for future transactions
          </span>
        </label>

        <div className="flex gap-3">
          <button onClick={handleSave} disabled={!category || !type}
            className="flex-1 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold py-2.5 rounded-lg transition-colors">
            Save Category
          </button>
          <button onClick={onClose}
            className="px-4 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm py-2.5 rounded-lg transition-colors">
            Skip
          </button>
        </div>
      </div>
    </div>
  );
}

// ── NeedsReviewPanel ────────────────────────────────────────────────────────
function NeedsReviewPanel({ transactions, userCategories, onReview }) {
  const [reviewingIdx, setReviewingIdx] = useState(null);
  if (transactions.length === 0) return null;
  const current = reviewingIdx !== null ? transactions[reviewingIdx] : null;

  return (
    <>
      <div className="bg-amber-950/50 border border-amber-700/60 rounded-xl p-4">
        <div className="flex items-center gap-3 mb-3">
          <AlertTriangle size={16} className="text-amber-400 flex-shrink-0" />
          <span className="text-sm font-semibold text-amber-300">
            {transactions.length} transaction{transactions.length !== 1 ? 's' : ''} need{transactions.length === 1 ? 's' : ''} review
          </span>
          <span className="ml-auto text-xs text-amber-500">Click any to categorize →</span>
        </div>
        <div className="space-y-2">
          {transactions.map((txn, i) => (
            <button key={txn.id} onClick={() => setReviewingIdx(i)}
              className="w-full flex items-center gap-3 p-2.5 bg-amber-900/20 hover:bg-amber-900/40 border border-amber-700/30 rounded-lg text-left transition-colors group"
            >
              <div className="flex-1 min-w-0">
                <span className="text-sm text-slate-200 font-medium truncate block">{txn.description}</span>
                <span className="text-xs text-slate-500">
                  {new Date(txn.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              </div>
              <span className="text-sm font-semibold text-rose-400 tabular-nums flex-shrink-0">{fmtD(txn.amount)}</span>
              <span className="text-xs text-amber-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">Review →</span>
            </button>
          ))}
        </div>
      </div>
      {current && (
        <ReviewTransactionModal
          transaction={current}
          userCategories={userCategories}
          onSave={(result) => { onReview(current.id, result); setReviewingIdx(null); }}
          onClose={() => setReviewingIdx(null)}
        />
      )}
    </>
  );
}

// ── TransactionList ─────────────────────────────────────────────────────────
function TransactionList({ transactions, onDelete, onReview, userCategories }) {
  const [showAll,      setShowAll]      = useState(false);
  const [reviewingTxn, setReviewingTxn] = useState(null);

  const sorted    = [...transactions].sort((a, b) => new Date(b.date) - new Date(a.date));
  const displayed = showAll ? sorted : sorted.slice(0, 10);

  if (sorted.length === 0) {
    return <p className="text-center text-slate-500 text-sm py-6">No transactions for this period</p>;
  }

  return (
    <div>
      <div className="space-y-0.5">
        {displayed.map(txn => {
          const tc = txn.type ? TYPE_COLORS[txn.type] : null;
          return (
            <div key={txn.id}
              className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-700/30 rounded-lg group transition-colors"
            >
              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                txn.kind === 'income' ? 'bg-emerald-900/50 text-emerald-400' : 'bg-rose-900/40 text-rose-400'
              }`}>
                {txn.kind === 'income'
                  ? <ArrowDownCircle size={14} />
                  : <ArrowUpCircle   size={14} />
                }
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-sm text-slate-200 font-medium truncate">{txn.description}</span>
                  {txn.category && tc && (
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${tc.bg} ${tc.text}`}>
                      {txn.category}
                    </span>
                  )}
                  {txn.source === 'auto' && (
                    <span className="text-xs flex items-center gap-0.5 text-blue-400/70 flex-shrink-0">
                      <Zap size={8} /> Auto
                    </span>
                  )}
                  {!txn.reviewed && isExpense(txn) && (
                    <button
                      onClick={() => setReviewingTxn(txn)}
                      className="text-xs text-amber-400 bg-amber-900/30 hover:bg-amber-900/60 px-1.5 py-0.5 rounded font-medium flex-shrink-0 transition-colors cursor-pointer"
                    >
                      Needs Review
                    </button>
                  )}
                </div>
                <span className="text-xs text-slate-500">
                  {new Date(txn.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`text-sm font-semibold tabular-nums ${
                  txn.kind === 'income' ? 'text-emerald-400' : 'text-slate-200'
                }`}>
                  {txn.kind === 'income' ? '+' : '-'}{fmtD(txn.amount)}
                </span>
                <button onClick={() => onDelete(txn.id)}
                  className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-rose-900/40 transition-all">
                  <Trash2 size={11} className="text-rose-400" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {sorted.length > 10 && (
        <button onClick={() => setShowAll(s => !s)}
          className="w-full mt-3 text-xs text-blue-400 hover:text-blue-300 py-2 text-center transition-colors">
          {showAll ? 'Show fewer transactions' : `Show all ${sorted.length} transactions`}
        </button>
      )}
      {reviewingTxn && (
        <ReviewTransactionModal
          transaction={reviewingTxn}
          userCategories={userCategories || []}
          onSave={(result) => { onReview(reviewingTxn.id, result); setReviewingTxn(null); }}
          onClose={() => setReviewingTxn(null)}
        />
      )}
    </div>
  );
}

// ── SpendingInsights ────────────────────────────────────────────────────────
function SpendingInsights({ transactions, allTransactions, income, totalBudgeted, period }) {
  const now         = new Date();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const daysPassed  = now.getDate();
  const daysLeft    = daysInMonth - daysPassed;

  const periodExpenses = transactions.filter(t => isExpense(t));
  const totalSpent     = periodExpenses.reduce((s, t) => s + t.amount, 0);

  // Top spending category
  const catTotals = {};
  periodExpenses.forEach(t => { if (t.category) catTotals[t.category] = (catTotals[t.category] || 0) + t.amount; });
  const topCat = Object.entries(catTotals).sort((a, b) => b[1] - a[1])[0];

  // Last month comparison (uses allTransactions so period filter doesn't affect it)
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthEnd   = new Date(now.getFullYear(), now.getMonth(), 0);
  const lastMonthSpent = (allTransactions || [])
    .filter(t => {
      const d = new Date(t.date + 'T12:00:00');
      return d >= lastMonthStart && d <= lastMonthEnd && isExpense(t);
    })
    .reduce((s, t) => s + t.amount, 0);

  // Projected end-of-month spend
  const projectedEOM = period === 'month' && daysPassed > 0 ? (totalSpent / daysPassed) * daysInMonth : null;

  // Uncategorized in current period
  const unrevCount = transactions.filter(t => !t.reviewed && isExpense(t)).length;

  const pctBudget = totalBudgeted > 0 ? (totalSpent / totalBudgeted * 100) : 0;

  const insights = [];

  if (period === 'month' && totalBudgeted > 0 && daysPassed > 0) {
    const color = pctBudget > 100 ? 'text-rose-400' : pctBudget > 80 ? 'text-amber-400' : 'text-emerald-400';
    insights.push({ icon: '📊', text: `You've used ${pctBudget.toFixed(0)}% of your monthly budget with ${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining.`, color });
  }

  if (topCat) {
    insights.push({ icon: '🏆', text: `Top spending category: ${topCat[0]} at ${fmt(topCat[1])}.`, color: 'text-blue-400' });
  }

  if (period === 'month' && lastMonthSpent > 0 && totalSpent > 0) {
    const diff = totalSpent - lastMonthSpent;
    const pct  = Math.abs(diff / lastMonthSpent * 100).toFixed(0);
    insights.push({
      icon: diff > 0 ? '📈' : '📉',
      text: `Compared to last month, you've spent ${pct}% ${diff > 0 ? 'more' : 'less'} (${diff > 0 ? '+' : ''}${fmt(diff)}).`,
      color: diff > 0 ? 'text-rose-400' : 'text-emerald-400',
    });
  }

  if (period === 'month' && projectedEOM !== null && daysPassed >= 3 && totalSpent > 0) {
    const overPace = totalBudgeted > 0 && projectedEOM > totalBudgeted;
    insights.push({
      icon: '🔮',
      text: `At your current pace, you'll spend approximately ${fmt(projectedEOM)} by month-end.`,
      color: overPace ? 'text-amber-400' : 'text-slate-300',
    });
  }

  if (unrevCount > 0) {
    insights.push({
      icon: '⚠️',
      text: `You have ${unrevCount} uncategorized transaction${unrevCount !== 1 ? 's' : ''} to review.`,
      color: 'text-amber-400',
    });
  }

  if (insights.length === 0) return null;

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
      <h2 className="font-semibold text-slate-200 text-base mb-4 flex items-center gap-2">
        💡 Spending Insights
      </h2>
      <div className="space-y-2">
        {insights.map((ins, i) => (
          <div key={i} className="flex items-start gap-3 p-3 bg-slate-700/30 rounded-lg">
            <span className="text-base flex-shrink-0 leading-tight mt-0.5">{ins.icon}</span>
            <p className={`text-sm leading-relaxed ${ins.color}`}>{ins.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── MonthInReview ─────────────────────────────────────────────────────────────
function MonthInReview({ data, monthLabel }) {
  const { totalIncomeReceived, totalSpentMonth, totalSaved, savingsRate, txnCount, mostExpensive } = data;

  return (
    <>
      {/* ── Print CSS (visibility-based so it works at any DOM depth) ── */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .mir-printable, .mir-printable * { visibility: visible !important; }
          .mir-printable {
            position: fixed !important; inset: 0 !important;
            background: white !important; padding: 48px !important;
            color: #0f172a !important; z-index: 99999 !important;
          }
          .mir-print-hide { display: none !important; }
          .mir-print-header { display: flex !important; }
          .mir-stat-card { background: #f8fafc !important; border: 1px solid #e2e8f0 !important; }
          .mir-stat-label { color: #475569 !important; }
          .mir-stat-value { color: #0f172a !important; }
        }
        @media screen { .mir-print-header { display: none !important; } }
      `}</style>

      <div className="mir-printable bg-slate-800 border border-slate-700 rounded-xl p-5">
        {/* Print-only header */}
        <div className="mir-print-header items-center justify-between mb-6 pb-5 border-b border-slate-300">
          <div>
            <div className="text-2xl font-black text-slate-900 tracking-tight">Finovo</div>
            <div className="text-sm text-slate-500 mt-0.5">Personal Finance Dashboard</div>
          </div>
          <div className="text-right">
            <div className="text-base font-semibold text-slate-700">Month in Review</div>
            <div className="text-sm text-slate-500">{monthLabel}</div>
          </div>
        </div>

        {/* Card header */}
        <div className="flex items-start justify-between mb-5">
          <div>
            <h2 className="font-semibold text-slate-200 text-base">Month in Review</h2>
            <p className="text-xs text-slate-500 mt-0.5">{monthLabel} · How did you do?</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Print button */}
            <button
              onClick={() => window.print()}
              className="mir-print-hide flex items-center gap-1.5 text-xs bg-slate-700 hover:bg-slate-600 border border-slate-600 text-slate-300 px-3 py-2 rounded-lg transition-colors"
            >
              🖨 Save as PDF
            </button>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
          <div className="mir-stat-card bg-slate-700/40 rounded-xl p-3.5">
            <div className="mir-stat-label text-xs text-slate-500 mb-1.5">Income Received</div>
            <div className="mir-stat-value text-xl font-bold text-emerald-400">{fmtD(totalIncomeReceived)}</div>
          </div>
          <div className="mir-stat-card bg-slate-700/40 rounded-xl p-3.5">
            <div className="mir-stat-label text-xs text-slate-500 mb-1.5">Total Spent</div>
            <div className="mir-stat-value text-xl font-bold text-rose-400">{fmtD(totalSpentMonth)}</div>
          </div>
          <div className="mir-stat-card bg-slate-700/40 rounded-xl p-3.5">
            <div className="mir-stat-label text-xs text-slate-500 mb-1.5">Net Saved</div>
            <div className={`mir-stat-value text-xl font-bold ${totalSaved >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
              {totalSaved >= 0 ? '+' : ''}{fmtD(totalSaved)}
            </div>
          </div>
          {savingsRate !== null && (
            <div className="mir-stat-card bg-slate-700/40 rounded-xl p-3.5">
              <div className="mir-stat-label text-xs text-slate-500 mb-1.5">Savings Rate</div>
              <div className={`mir-stat-value text-xl font-bold ${
                savingsRate >= 20 ? 'text-emerald-400'
                : savingsRate >= 10 ? 'text-amber-400'
                : 'text-rose-400'
              }`}>
                {savingsRate.toFixed(1)}%
              </div>
            </div>
          )}
          <div className="mir-stat-card bg-slate-700/40 rounded-xl p-3.5">
            <div className="mir-stat-label text-xs text-slate-500 mb-1.5">Transactions</div>
            <div className="mir-stat-value text-xl font-bold text-slate-200">{txnCount}</div>
          </div>
          {mostExpensive && (
            <div className="mir-stat-card bg-slate-700/40 rounded-xl p-3.5 col-span-2 sm:col-span-1">
              <div className="mir-stat-label text-xs text-slate-500 mb-1.5">Biggest Purchase</div>
              <div className="mir-stat-value text-sm font-bold text-slate-200 truncate">{mostExpensive.description}</div>
              <div className="text-sm font-semibold text-rose-300 tabular-nums mt-0.5">{fmtD(mostExpensive.amount)}</div>
            </div>
          )}
        </div>

      </div>
    </>
  );
}

// ── Add Category Modal ──────────────────────────────────────────────────────
// AddCategoryModal — step 1: pick from preset grid; step 2: set budget amount
function AddCategoryModal({ onClose, onAdd, existingCategoryNames = [] }) {
  const [step,        setStep]     = useState(1);           // 1 = pick, 2 = set amount
  const [selected,    setSelected] = useState(null);        // { name, icon, type }
  const [customName,  setCustomName]  = useState('');
  const [budgeted,    setBudgeted] = useState('');

  // Hide categories user already has
  const alreadyAdded = new Set(existingCategoryNames.map(n => n.toLowerCase()));

  const handleSelect = (cat) => {
    setSelected(cat);
    setStep(2);
  };

  const handleSelectOther = () => {
    setSelected(OTHER_CATEGORY);
    setCustomName('');
    setStep(2);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const b = parseInt(budgeted.replace(/[^0-9]/g, ''), 10);
    if (isNaN(b) || b <= 0) return;
    const isCustom = selected?.name === 'Other';
    const name = isCustom ? customName.trim() : selected?.name;
    if (!name) return;
    const cat = isCustom
      ? { name, icon: '🔖', type: 'Want' }
      : { name: selected.name, icon: selected.icon, type: selected.type };
    onAdd({ ...cat, budgeted: b, spent: 0 });
    onClose();
  };

  // Group standard categories by group
  const groups = ['Needs', 'Wants', 'Savings'];
  const grouped = groups.map(g => ({
    label: g,
    cats: STANDARD_CATEGORIES.filter(c => c.group === g && !alreadyAdded.has(c.name.toLowerCase())),
  })).filter(g => g.cats.length > 0);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}>
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl"
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-slate-700">
          <div className="flex items-center gap-2">
            {step === 2 && (
              <button onClick={() => setStep(1)}
                className="p-1 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-slate-200 transition-colors mr-1">
                <X size={14} className="rotate-45" />
              </button>
            )}
            <h3 className="font-bold text-slate-100 text-lg">
              {step === 1 ? 'Add Budget Category' : `${selected?.icon} ${selected?.name === 'Other' ? 'Custom Category' : selected?.name}`}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors">
            <X size={16} className="text-slate-400" />
          </button>
        </div>

        {step === 1 ? (
          <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
            <p className="text-xs text-slate-500 mb-4">
              Pick a standard category — icon and 50/30/20 type are pre-set so Plaid transactions match automatically.
            </p>
            <div className="space-y-5">
              {grouped.map(({ label, cats }) => (
                <div key={label}>
                  <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${
                    label === 'Needs' ? 'text-blue-400' : label === 'Wants' ? 'text-purple-400' : 'text-emerald-400'
                  }`}>{label}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {cats.map(cat => (
                      <button key={cat.name} onClick={() => handleSelect(cat)}
                        className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-slate-700/60 hover:bg-slate-700 border border-slate-600/60 hover:border-slate-500 transition-all text-left group">
                        <span className="text-xl flex-shrink-0">{cat.icon}</span>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-200 leading-tight truncate">{cat.name}</p>
                          <p className={`text-xs ${
                            cat.type === 'Need' ? 'text-blue-400/70' : cat.type === 'Want' ? 'text-purple-400/70' : 'text-emerald-400/70'
                          }`}>{cat.type}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {/* Other */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider mb-2 text-slate-500">Custom</p>
                <button onClick={handleSelectOther}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-slate-700/40 hover:bg-slate-700 border border-dashed border-slate-600/60 hover:border-slate-500 transition-all text-left">
                  <span className="text-xl">🔖</span>
                  <div>
                    <p className="text-sm font-medium text-slate-400">Other (custom name)</p>
                    <p className="text-xs text-slate-600">Won't auto-match Plaid transactions</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            {selected?.name === 'Other' && (
              <div>
                <label className="text-xs text-slate-400 mb-1.5 block">Category Name</label>
                <input value={customName} onChange={e => setCustomName(e.target.value)}
                  placeholder="e.g. Pet Expenses" autoFocus required
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2.5 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
            )}
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Monthly Budget</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-semibold">$</span>
                <input value={budgeted} onChange={e => setBudgeted(e.target.value)}
                  placeholder="0" type="number" min="1" autoFocus={selected?.name !== 'Other'} required
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg pl-7 pr-3 py-2.5 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex gap-3 pt-1">
              <button type="submit"
                className="flex-1 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors">
                Add Category
              </button>
              <button type="button" onClick={() => setStep(1)}
                className="px-4 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm py-2.5 rounded-lg transition-colors">
                Back
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ── AddBillModal ─────────────────────────────────────────────────────────────
function AddBillModal({ onClose, onAdd }) {
  const [name,      setName]      = useState('');
  const [amount,    setAmount]    = useState('');
  const [dueDay,    setDueDay]    = useState('1');
  const [frequency, setFrequency] = useState('monthly');
  const [category,  setCategory]  = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const amt = parseFloat(amount.replace(/[^0-9.]/g, ''));
    const day = parseInt(dueDay, 10);
    if (!name.trim() || isNaN(amt) || amt <= 0 || isNaN(day) || day < 1 || day > 31) return;
    onAdd({ name: name.trim(), amount: amt, dueDay: day, frequency, category });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-sm p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-slate-100 text-lg">Add Bill</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors">
            <X size={16} className="text-slate-400" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Bill Name</label>
            <input
              value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. Rent, Electric, Netflix" autoFocus required
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2.5
                         text-slate-100 text-sm focus:outline-none focus:border-blue-500 placeholder:text-slate-600"
            />
          </div>
          {/* Amount + Due Day */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Amount</label>
              <div className="flex items-center gap-1.5 bg-slate-700 border border-slate-600 rounded-lg px-3 py-2.5 focus-within:border-blue-500 transition-colors">
                <span className="text-slate-400 text-sm">$</span>
                <input
                  type="number" value={amount} onChange={e => setAmount(e.target.value)}
                  placeholder="0.00" min="0" step="0.01" required
                  className="flex-1 bg-transparent text-slate-100 text-sm focus:outline-none tabular-nums placeholder:text-slate-600"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Due Day (1–31)</label>
              <input
                type="number" value={dueDay} onChange={e => setDueDay(e.target.value)}
                min="1" max="31" required
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2.5
                           text-slate-100 text-sm focus:outline-none focus:border-blue-500 tabular-nums"
              />
            </div>
          </div>
          {/* Frequency */}
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Frequency</label>
            <select value={frequency} onChange={e => setFrequency(e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2.5
                         text-slate-100 text-sm focus:outline-none focus:border-blue-500">
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annual">Annual</option>
            </select>
          </div>
          {/* Category */}
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">
              Category <span className="text-slate-600 normal-case">(optional)</span>
            </label>
            <select value={category} onChange={e => setCategory(e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2.5
                         text-slate-100 text-sm focus:outline-none focus:border-blue-500">
              <option value="">— None —</option>
              {ALL_CATEGORIES.map(c => (
                <option key={c.name} value={c.name}>{c.icon} {c.name}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-3 pt-1">
            <button type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors">
              Add Bill
            </button>
            <button type="button" onClick={onClose}
              className="px-4 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm py-2.5 rounded-lg transition-colors">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── CategoryRow ─────────────────────────────────────────────────────────────
function CategoryRow({ cat, spent, income, onSaveBudget, onLogSpending, onDelete, rollover = 0, rolloverEnabled = false, onToggleRollover, goals = [], goalLink = null, onSetGoalLink }) {
  const [editing,    setEditing]    = useState(false);
  const [inputVal,   setInputVal]   = useState(String(cat.budgeted));
  const [spendInput, setSpendInput] = useState('');

  const effectiveBudget = cat.budgeted + rollover;
  const over     = spent > effectiveBudget;
  const under    = spent < effectiveBudget;
  const diff     = spent - effectiveBudget;
  const barPct   = effectiveBudget > 0 ? Math.min((spent / effectiveBudget) * 100, 110) : 0;
  const barColor = over ? 'bg-rose-500' : barPct > 80 ? 'bg-amber-400' : 'bg-blue-500';

  const bench    = BENCHMARKS[cat.name];
  const benchAmt = bench && income > 0 ? Math.round(income * bench.pct / 100) : null;

  const openEdit       = () => { setInputVal(String(cat.budgeted)); setEditing(true); };
  const handleSaveBudget = () => {
    const val = parseInt(inputVal.replace(/[^0-9]/g, ''), 10);
    if (!isNaN(val) && val > 0) onSaveBudget(val);
    setEditing(false);
  };
  const handleLogSpend = () => {
    const amt = parseFloat(spendInput.replace(/[^0-9.]/g, ''));
    if (!isNaN(amt) && amt > 0) { onLogSpending(amt); setSpendInput(''); }
  };

  return (
    <div className="border-b border-slate-700/50 last:border-0">
      <div
        className="flex items-center gap-4 py-3 cursor-pointer group hover:bg-slate-700/20 rounded-lg px-2 -mx-2 transition-colors"
        onClick={() => { if (!editing) openEdit(); }}
      >
        <div className="w-7 text-base flex-shrink-0">{cat.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-sm font-medium text-slate-200">{cat.name}</span>
              <Pencil size={10} className="text-slate-600 group-hover:text-slate-400 transition-colors" />
              {rollover > 0 && (
                <span className="text-xs text-emerald-400 bg-emerald-900/30 border border-emerald-700/30 px-1.5 py-0.5 rounded-md font-medium">
                  +{fmt(rollover)} rollover
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className="text-slate-400">{fmt(spent)}</span>
              <span className="text-slate-600">/</span>
              <span className="text-slate-500 tabular-nums">{fmt(effectiveBudget)}</span>
              <span className={`flex items-center gap-0.5 font-medium text-xs min-w-[64px] text-right ${
                over ? 'text-rose-400' : under ? 'text-emerald-400' : 'text-slate-500'
              }`}>
                {over  && <><TrendingUp  size={11} /> +{fmt(Math.abs(diff))}</>}
                {under && <><TrendingDown size={11} /> {fmt(Math.abs(diff))} left</>}
                {!over && !under && <><Minus size={11} /> on track</>}
              </span>
            </div>
          </div>
          {rollover > 0 && (
            <p className="text-xs text-slate-500 mb-1">includes {fmt(rollover)} from last month</p>
          )}
          <div className="w-full bg-slate-700 rounded-full h-1.5 overflow-hidden">
            <div className={`h-1.5 rounded-full transition-all ${barColor}`} style={{ width: `${Math.min(barPct, 100)}%` }} />
          </div>
          {/* Goal link contextual message */}
          {(() => {
            const linkedGoal = goalLink ? goals.find(g => g.id === goalLink) : null;
            if (!linkedGoal) return null;
            const remaining = Math.abs(diff);
            if (under && diff < -0.5) {
              return (
                <p className="text-xs text-emerald-400/80 mt-1 leading-snug">
                  🎯 {fmt(remaining)} saved here → one step closer to {linkedGoal.icon} {linkedGoal.name}!
                </p>
              );
            }
            if (over) {
              return (
                <p className="text-xs text-amber-400/80 mt-1 leading-snug">
                  ↕ {fmt(remaining)} over — adjust next month to stay on track for {linkedGoal.icon} {linkedGoal.name}.
                </p>
              );
            }
            return (
              <p className="text-xs text-slate-500 mt-1 leading-snug">
                🎯 Linked to {linkedGoal.icon} {linkedGoal.name}
              </p>
            );
          })()}
        </div>
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="opacity-0 group-hover:opacity-100 p-1 rounded-lg hover:bg-rose-900/40 transition-all flex-shrink-0">
          <Trash2 size={12} className="text-rose-400" />
        </button>
      </div>

      {editing && (
        <div className="mx-1 mb-4 p-4 bg-slate-700/40 rounded-xl border border-slate-600/40 space-y-3" onClick={e => e.stopPropagation()}>
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-[160px]">
              <label className="text-xs text-slate-400 mb-1.5 block font-medium">Monthly goal for {cat.name}</label>
              <div className="flex items-center gap-2">
                <span className="text-slate-400 text-sm font-semibold">$</span>
                <input type="number" value={inputVal} onChange={e => setInputVal(e.target.value)} min="0"
                  onKeyDown={e => { if (e.key === 'Enter') handleSaveBudget(); if (e.key === 'Escape') setEditing(false); }}
                  className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm w-32 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 tabular-nums"
                  autoFocus
                />
              </div>
            </div>
            {bench && benchAmt && (
              <div className="flex-1 min-w-[160px]">
                <div className="text-xs text-slate-500 mb-1.5 font-medium">Benchmark for your income</div>
                <button onClick={() => setInputVal(String(benchAmt))}
                  className="text-xs text-blue-400 hover:text-blue-300 font-semibold bg-blue-900/20 border border-blue-700/30 px-3 py-1.5 rounded-lg transition-colors">
                  Use {fmt(benchAmt)} <span className="font-normal text-blue-400/70">({bench.range})</span>
                </button>
              </div>
            )}
          </div>

          {bench && benchAmt && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-slate-500">Quick set:</span>
              {[
                { label: fmt(Math.round(benchAmt * 0.85)), val: Math.round(benchAmt * 0.85) },
                { label: fmt(benchAmt),                    val: benchAmt,                    tag: 'benchmark' },
                { label: fmt(Math.round(benchAmt * 1.1)),  val: Math.round(benchAmt * 1.1) },
                { label: fmt(Math.round(benchAmt * 1.25)), val: Math.round(benchAmt * 1.25) },
              ].map(p => (
                <button key={p.val} onClick={() => setInputVal(String(p.val))}
                  className={`text-xs px-2.5 py-1 rounded-md transition-colors ${
                    p.tag === 'benchmark'
                      ? 'bg-blue-700/40 hover:bg-blue-700/60 text-blue-300 border border-blue-700/40'
                      : 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                  }`}
                >{p.label}{p.tag ? ' ✓' : ''}</button>
              ))}
            </div>
          )}

          {bench && (
            <div className="flex items-start gap-2 text-xs text-slate-400 bg-slate-800/60 rounded-lg p-3 leading-relaxed">
              <Info size={12} className="text-blue-400 flex-shrink-0 mt-0.5" />
              <span>{bench.tip}</span>
            </div>
          )}

          {/* Quick log spending */}
          <div className="border-t border-slate-700/50 pt-3">
            <label className="text-xs text-slate-400 mb-1.5 block font-medium">Quick-log a spend for {cat.name}</label>
            <div className="flex items-center gap-2">
              <span className="text-slate-400 text-sm font-semibold">$</span>
              <input type="number" value={spendInput} onChange={e => setSpendInput(e.target.value)} min="0"
                onKeyDown={e => { if (e.key === 'Enter') handleLogSpend(); }}
                placeholder="0" className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm w-28 focus:outline-none focus:border-emerald-500 tabular-nums"
              />
              <button onClick={handleLogSpend}
                className="flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-semibold px-3 py-2 rounded-lg transition-colors">
                <Plus size={11} /> Log
              </button>
            </div>
          </div>

          {/* Rollover toggle */}
          <div className="border-t border-slate-700/50 pt-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <button
                type="button"
                onClick={() => onToggleRollover && onToggleRollover(!rolloverEnabled)}
                className={`mt-0.5 w-10 h-5 rounded-full flex-shrink-0 transition-all relative ${
                  rolloverEnabled ? 'bg-emerald-600' : 'bg-slate-600'
                }`}
              >
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${
                  rolloverEnabled ? 'left-5' : 'left-0.5'
                }`} />
              </button>
              <div>
                <span className="text-sm font-medium text-slate-200">Roll over unused budget</span>
                <p className="text-xs text-slate-500 mt-0.5">
                  {rolloverEnabled
                    ? 'Unspent budget carries forward to next month automatically.'
                    : 'Budget resets to base amount each month.'}
                </p>
              </div>
            </label>
            {rolloverEnabled && rollover > 0 && (
              <div className="mt-2 text-xs text-emerald-400 bg-emerald-900/20 border border-emerald-700/30 rounded-lg px-3 py-2 flex items-center gap-1.5">
                <Check size={11} />
                {fmt(rollover)} rolled over from last month — effective budget is {fmt(effectiveBudget)}
              </div>
            )}
          </div>

          {/* Goal link */}
          {goals.length > 0 && (
            <div className="border-t border-slate-700/50 pt-3">
              <label className="text-xs text-slate-400 mb-1.5 block font-medium">
                Link to a financial goal <span className="text-slate-600 font-normal">(optional)</span>
              </label>
              <select
                value={goalLink || ''}
                onChange={e => onSetGoalLink && onSetGoalLink(e.target.value || null)}
                onClick={e => e.stopPropagation()}
                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-slate-200 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30"
              >
                <option value="">— No linked goal —</option>
                {goals.map(g => (
                  <option key={g.id} value={g.id}>{g.icon} {g.name}</option>
                ))}
              </select>
              {goalLink && (() => {
                const lg = goals.find(g => g.id === goalLink);
                if (!lg) return null;
                const pct2 = lg.target > 0 ? Math.min(Math.round((lg.current / lg.target) * 100), 100) : 0;
                return (
                  <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
                    <div className="flex-1 bg-slate-700 rounded-full h-1.5 overflow-hidden">
                      <div className="h-1.5 rounded-full bg-blue-500" style={{ width: `${pct2}%` }} />
                    </div>
                    <span className="text-slate-500 whitespace-nowrap">{pct2}% of {lg.name}</span>
                  </div>
                );
              })()}
            </div>
          )}

          <div className="flex items-center gap-2 pt-0.5">
            <button onClick={handleSaveBudget}
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors">
              <Check size={12} /> Save Budget
            </button>
            <button onClick={() => setEditing(false)}
              className="flex items-center gap-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs px-3 py-1.5 rounded-lg transition-colors">
              <X size={12} /> Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── SmartNudgesPanel ─────────────────────────────────────────────────────────
const NUDGE_COLORS = {
  amber:   { bg: 'bg-amber-950/50',   border: 'border-amber-700/50',   text: 'text-amber-200',   dim: 'text-amber-400'   },
  rose:    { bg: 'bg-rose-950/50',    border: 'border-rose-700/50',    text: 'text-rose-200',    dim: 'text-rose-400'    },
  emerald: { bg: 'bg-emerald-950/40', border: 'border-emerald-700/40', text: 'text-emerald-200', dim: 'text-emerald-400' },
};

function SmartNudgesPanel({ nudges, onDismiss }) {
  if (!nudges || nudges.length === 0) return null;
  return (
    <div className="space-y-2">
      {nudges.map(nudge => {
        const cl = NUDGE_COLORS[nudge.color] || NUDGE_COLORS.amber;
        return (
          <div key={nudge.id}
            className={`flex items-start gap-3 px-4 py-3 rounded-xl border ${cl.bg} ${cl.border}`}>
            <span className="text-base flex-shrink-0 leading-tight mt-0.5">{nudge.icon}</span>
            <p className={`flex-1 text-sm leading-relaxed ${cl.text}`}>{nudge.text}</p>
            <button
              onClick={() => onDismiss(nudge.id)}
              className={`flex-shrink-0 p-1 rounded-lg ${cl.dim} hover:text-slate-200 transition-colors`}
              aria-label="Dismiss"
            >
              <X size={13} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ── Streak constants + badge ─────────────────────────────────────────────
const STREAK_MILESTONES = {
  7:  { label: 'One week!',      icon: '🔥' },
  14: { label: 'Two weeks!',     icon: '🔥' },
  30: { label: 'One month!',     icon: '⭐' },
  60: { label: 'Two months!',    icon: '🌟' },
  90: { label: 'Three months!',  icon: '🏆' },
};

function StreakBadge({ streakDays }) {
  if (streakDays < 2) return null;
  const topMilestone = [90, 60, 30, 14, 7].find(m => streakDays >= m);
  const icon = streakDays >= 30 ? (streakDays >= 60 ? '🌟' : '⭐') : '🔥';
  return (
    <div
      title={`You've logged transactions for ${streakDays} consecutive day${streakDays !== 1 ? 's' : ''}!`}
      className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-900/30 border border-amber-700/40 rounded-xl text-amber-300 text-sm font-medium select-none"
    >
      <span className="text-base leading-none">{icon}</span>
      <span className="tabular-nums">{streakDays}-day streak</span>
      {topMilestone && (
        <span className="text-amber-400/70 text-xs font-normal hidden sm:inline">
          · {STREAK_MILESTONES[topMilestone].label}
        </span>
      )}
    </div>
  );
}

// ── Confetti + Celebration constants ──────────────────────────────────────
const CONFETTI_COLORS = ['#f43f5e','#fb923c','#fbbf24','#34d399','#60a5fa','#a78bfa','#f472b6','#22d3ee'];

const CONFETTI_CSS = `
@keyframes cf0{0%{transform:translateY(-12px) rotate(0deg);opacity:1}90%{opacity:.9}100%{transform:translateY(106vh) rotate(540deg);opacity:0}}
@keyframes cf1{0%{transform:translateY(-12px) rotate(40deg);opacity:1}90%{opacity:.9}100%{transform:translateY(106vh) rotate(-380deg);opacity:0}}
@keyframes cf2{0%{transform:translateY(-12px) rotate(80deg);opacity:1}90%{opacity:.9}100%{transform:translateY(106vh) rotate(680deg);opacity:0}}
@keyframes cf3{0%{transform:translateY(-12px) rotate(120deg);opacity:1}90%{opacity:.9}100%{transform:translateY(106vh) rotate(-500deg);opacity:0}}
@keyframes cf4{0%{transform:translateY(-12px) rotate(160deg);opacity:1}90%{opacity:.9}100%{transform:translateY(106vh) rotate(440deg);opacity:0}}
@keyframes cf5{0%{transform:translateY(-12px) rotate(200deg);opacity:1}90%{opacity:.9}100%{transform:translateY(106vh) rotate(-620deg);opacity:0}}
@keyframes toast-rise{0%{opacity:0;transform:translate(-50%,-20px) scale(.86)}60%{transform:translate(-50%,4px) scale(1.03)}100%{opacity:1;transform:translate(-50%,0) scale(1)}}
@keyframes toast-drop{0%{opacity:1;transform:translate(-50%,0)}100%{opacity:0;transform:translate(-50%,-16px)}}
`;

// Deterministic pseudo-random so particles are stable across renders
function _sr(seed) { const x = Math.sin(seed) * 10000; return x - Math.floor(x); }
const CONFETTI_PARTICLES = Array.from({ length: 52 }, (_, i) => ({
  id:     i,
  left:   `${2 + _sr(i * 7.31 + 1) * 96}%`,
  delay:  `${_sr(i * 3.77 + 2) * 0.85}s`,
  dur:    `${2.3 + _sr(i * 5.13 + 3) * 1.7}s`,
  color:  CONFETTI_COLORS[i % CONFETTI_COLORS.length],
  w:      _sr(i * 11.3 + 4) > 0.5 ? `${6 + _sr(i * 2.3) * 5}px` : `${3 + _sr(i * 4.1) * 3}px`,
  h:      `${6 + _sr(i * 6.7 + 5) * 8}px`,
  radius: _sr(i * 9.11 + 6) > 0.62 ? '50%' : '2px',
  anim:   `cf${i % 6}`,
}));

// ── Coach message copy ─────────────────────────────────────────────────────
const COACH_STATES = {
  fresh: {
    gradient: 'from-blue-950/70 to-violet-950/60', border: 'border-blue-700/40',
    textColor: 'text-blue-100', subColor: 'text-blue-300/70',
    messages: [
      { icon: '👋', text: "Welcome to your financial journey.", sub: "Every dollar you track is a step toward clarity and freedom." },
      { icon: '🌱', text: "You're building something powerful here.", sub: "Tracking your spending is the first step to owning your financial future." },
    ],
  },
  noData: {
    gradient: 'from-slate-800/80 to-slate-800/50', border: 'border-slate-600/40',
    textColor: 'text-slate-200', subColor: 'text-slate-400',
    messages: [
      { icon: '📝', text: "Your budget is set — now let's fill in the picture.", sub: "Log your first transaction to start seeing where your money really goes." },
      { icon: '🔍', text: "Tracking spending is the superpower most people skip.", sub: "Add a transaction to start unlocking your personalized insights." },
    ],
  },
  under: {
    gradient: 'from-emerald-950/70 to-teal-950/60', border: 'border-emerald-700/40',
    textColor: 'text-emerald-100', subColor: 'text-emerald-300/70',
    messages: [
      { icon: '🌟', text: "You're doing amazing this month!", sub: (left) => `${left} left and you're in full control. Keep it going.` },
      { icon: '💪', text: "Your budget game is seriously strong right now.", sub: (left) => `${left} remaining — you're ahead of where most people ever get.` },
      { icon: '🎯', text: "Crushing it!", sub: (left) => `${left} still in the budget. This is what financial confidence actually feels like.` },
      { icon: '✨', text: "Intentional and in control.", sub: (left) => `${left} to go. Your future self is going to thank you for this.` },
    ],
  },
  close: {
    gradient: 'from-amber-950/70 to-yellow-950/60', border: 'border-amber-700/40',
    textColor: 'text-amber-100', subColor: 'text-amber-300/70',
    messages: [
      { icon: '🧘', text: "You're being mindful with your money this month.", sub: "You're close to your limit — a few careful days and you finish strong." },
      { icon: '⚖️', text: "In the zone and keeping it together.", sub: "One more push to the finish line. You know what to do." },
      { icon: '🌤', text: "Almost at budget — stay the course.", sub: "A mindful finish is still a win. You've got this." },
    ],
  },
  over: {
    gradient: 'from-rose-950/60 to-slate-900/80', border: 'border-rose-800/40',
    textColor: 'text-rose-100', subColor: 'text-rose-300/60',
    messages: [
      { icon: '💙', text: "Tough month — but awareness is the first step.", sub: "Let's figure this out together. Knowing is already halfway there." },
      { icon: '🤝', text: "Every month is a new chance to reset.", sub: "Overspending happens to everyone. What matters is what you do next." },
      { icon: '🧭', text: "You tracked it — that takes real courage.", sub: "Most people don't even look. You did, and that's what changes things." },
    ],
  },
};

// ── localStorage helpers (module-level, no component dep) ─────────────────
function getCelebrated(key) {
  try { return !!JSON.parse(localStorage.getItem('finovo_celebrated') || '{}')[key]; } catch { return false; }
}
function markCelebrated(key) {
  try {
    const d = JSON.parse(localStorage.getItem('finovo_celebrated') || '{}');
    localStorage.setItem('finovo_celebrated', JSON.stringify({ ...d, [key]: true }));
  } catch {}
}

// ── CelebrationOverlay ────────────────────────────────────────────────────
function CelebrationOverlay({ celebration, onDone, onDisableAll }) {
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setExiting(true), 3100);
    const t2 = setTimeout(onDone, 3700);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [onDone]);

  const dismiss = () => { setExiting(true); setTimeout(onDone, 450); };

  return (
    <>
      <style>{CONFETTI_CSS}</style>
      {/* Confetti rain — pointer-events:none so page stays interactive */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 9990 }}>
        {CONFETTI_PARTICLES.map(p => (
          <div key={p.id} style={{
            position: 'absolute', left: p.left, top: '-14px',
            width: p.w, height: p.h, backgroundColor: p.color, borderRadius: p.radius,
            animation: `${p.anim} ${p.dur} ${p.delay} ease-in forwards`,
          }} />
        ))}
      </div>
      {/* Toast message */}
      <div className="fixed inset-x-0 top-14 flex justify-center pointer-events-none" style={{ zIndex: 9995 }}>
        <div
          style={{ animation: exiting ? 'toast-drop 0.45s ease-in forwards' : 'toast-rise 0.55s cubic-bezier(0.34,1.56,0.64,1) forwards' }}
          className="pointer-events-auto mx-4 max-w-xs w-full bg-gradient-to-r from-emerald-600 to-teal-500 text-white rounded-2xl shadow-2xl shadow-emerald-900/50 overflow-hidden"
        >
          <div className="flex items-start gap-3 px-5 py-4">
            <span className="text-2xl flex-shrink-0 mt-0.5">{celebration.icon}</span>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm leading-tight">{celebration.message}</div>
              {celebration.sub && <div className="text-xs text-white/75 mt-1 leading-snug">{celebration.sub}</div>}
            </div>
            <button onClick={dismiss} className="flex-shrink-0 p-1 rounded-lg hover:bg-white/20 transition-colors">
              <X size={13} />
            </button>
          </div>
          <div className="flex items-center justify-between px-5 pb-3 border-t border-white/20 pt-2">
            <span className="text-xs text-white/50">🎊 Finovo</span>
            <button onClick={() => { onDisableAll(); dismiss(); }}
              className="text-xs text-white/50 hover:text-white/80 underline transition-colors">
              Turn off celebrations
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── CoachMessage ──────────────────────────────────────────────────────────
function CoachMessage({ state, amountLeft, onDismiss }) {
  const conf = COACH_STATES[state] || COACH_STATES.fresh;
  // Rotate through messages daily so it doesn't feel static
  const msg  = conf.messages[new Date().getDate() % conf.messages.length];
  const sub  = typeof msg.sub === 'function' ? msg.sub(amountLeft) : msg.sub;

  return (
    <div className={`flex items-start gap-3 px-4 py-3.5 rounded-xl border bg-gradient-to-br relative overflow-hidden ${conf.gradient} ${conf.border}`}>
      {/* Subtle shimmer */}
      <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/[0.025] to-white/0 pointer-events-none" />
      <span className="text-xl flex-shrink-0 mt-0.5 relative z-10">{msg.icon}</span>
      <div className="flex-1 min-w-0 relative z-10">
        <p className={`text-sm font-semibold leading-snug ${conf.textColor}`}>{msg.text}</p>
        <p className={`text-xs mt-0.5 leading-relaxed ${conf.subColor}`}>{sub}</p>
      </div>
      <button onClick={onDismiss}
        className="flex-shrink-0 p-1 rounded-lg text-slate-500 hover:text-slate-300 transition-colors relative z-10"
        aria-label="Dismiss">
        <X size={13} />
      </button>
    </div>
  );
}

// ── Main Budget component ───────────────────────────────────────────────────
export default function Budget() {
  const { budgetData, goalsData, updateSection, dataLoading, linkedAccountsData, userProfile } = useUserData();

  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showAddTxn,      setShowAddTxn]      = useState(false);
  const [addTxnKind,      setAddTxnKind]      = useState('expense');
  const [editingIncome,   setEditingIncome]   = useState(false);
  const [incomeInput,     setIncomeInput]     = useState('');
  const [period,          setPeriod]          = useState('last30');
  const [showAddBill,     setShowAddBill]     = useState(false);
  const [dismissedNudges, setDismissedNudges] = useState([]);
  const [syncingPlaid,    setSyncingPlaid]    = useState(false);
  const [lastPlaidSync,   setLastPlaidSync]   = useState(null);
  // ── Celebration + coach state ──────────────────────────────────────────
  const [celebration,       setCelebration]       = useState(null);
  const [coachDismissed,    setCoachDismissed]    = useState(false);
  const [celebrationsEnabled, setCelebrationsEnabled] = useState(
    () => { try { return localStorage.getItem('finovo_celebrations') !== 'false'; } catch { return true; } }
  );
  const fireCelebration = useCallback((icon, message, sub) => {
    if (!celebrationsEnabled) return;
    setCelebration({ icon, message, sub });
  }, [celebrationsEnabled]);
  const disableCelebrations = useCallback(() => {
    setCelebrationsEnabled(false);
    try { localStorage.setItem('finovo_celebrations', 'false'); } catch {}
  }, []);
  // Refs for change-detection (set to null = "baseline not yet established")
  const mountedRef     = useRef(false);
  const prevTxnLen     = useRef(null);
  const prevSavingsRef = useRef(null);
  // (reCatRef removed — auto-recategorize now runs whenever uncategorized txns exist)

  const income             = budgetData.income             || 0;
  const categories         = budgetData.categories         || [];
  // Memoize to keep stable array reference when budgetData.transactions is undefined
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const transactions       = useMemo(() => budgetData.transactions    || [], [budgetData.transactions]);
  const categoryRules      = budgetData.categoryRules      || {};
  const rolloverEnabled    = budgetData.rolloverEnabled    || {};
  const rollovers          = budgetData.rollovers          || {};
  const categoryGoalLinks  = budgetData.categoryGoalLinks  || {};
  const goals              = useMemo(() => goalsData || [], [goalsData]);

  // ── Date range ──────────────────────────────────────────────────────────
  const { start: periodStart, end: periodEnd } = useMemo(() => getDateRange(period), [period]);

  const filteredTxns = useMemo(() => transactions.filter(t => {
    const d = new Date(t.date + 'T12:00:00');
    return d >= periodStart && d <= periodEnd;
  }), [transactions, periodStart, periodEnd]);

  // Uncategorized = expenses with NO category set (not period-filtered)
  const uncategorizedTxns = useMemo(() =>
    transactions.filter(t => !t.category && isExpense(t)),
    [transactions]
  );

  // Spending by category in period — include all expense txns with a category,
  // even if unreviewed (Plaid auto-categorized) so real data shows immediately.
  const txnSpentByCategory = useMemo(() => {
    const r = {};
    const catNames = new Set(categories.map(c => c.name));
    filteredTxns.filter(t => isExpense(t) && t.category)
      .forEach(t => {
        let cat = t.category;
        if (!catNames.has(cat)) {
          const stdType = t.type
            || STANDARD_CATEGORIES.find(s => s.name === cat)?.type
            || null;
          cat = mapToUserCategory(cat, stdType, categories);
        }
        r[cat] = (r[cat] || 0) + t.amount;
      });
    return r;
  }, [filteredTxns, categories]);

  // Bucket actual spending — covers both typed (Need/Want/Savings) and category-mapped transactions.
  // Include all expense transactions (Plaid unreviewed ones count toward real 50/30/20 totals).
  const bucketActual = useMemo(() => {
    const r = { needs: 0, wants: 0, savings: 0 };
    filteredTxns.filter(t => isExpense(t)).forEach(t => {
      let bucket;
      if (t.type === 'Need')    bucket = 'needs';
      else if (t.type === 'Want')    bucket = 'wants';
      else if (t.type === 'Savings') bucket = 'savings';
      else if (t.category)           bucket = RULE_BUCKETS[t.category];
      if (bucket) r[bucket] += t.amount;
    });
    return r;
  }, [filteredTxns]);

  // Count expense transactions that still need review (no category/type assigned).
  // These are already excluded from bucketActual naturally; we surface them as a note.
  const unreviewedExpenseCount = useMemo(
    () => filteredTxns.filter(t => isExpense(t) && !t.reviewed).length,
    [filteredTxns]
  );

  // Monthly chart data (year view)
  const monthlyChartData = useMemo(() => {
    if (period !== 'year') return [];
    const now = new Date();
    return Array.from({ length: 12 }, (_, i) => {
      const mStart = new Date(now.getFullYear(), i, 1);
      const mEnd   = new Date(now.getFullYear(), i + 1, 0);
      const mSpent = transactions
        .filter(t => { const d = new Date(t.date + 'T12:00:00'); return d >= mStart && d <= mEnd && isExpense(t); })
        .reduce((s, t) => s + t.amount, 0);
      return { month: mStart.toLocaleDateString('en-US', { month: 'short' }), spent: mSpent };
    });
  }, [period, transactions]);

  // getCategorySpent: prefer transaction-based, fall back to cat.spent
  const getCategorySpent = (catName) => {
    if (transactions.length > 0) return txnSpentByCategory[catName] || 0;
    const cat = categories.find(c => c.name === catName);
    return cat ? (cat.spent || 0) : 0;
  };

  // ── Derived totals ────────────────────────────────────────────────────
  const totalBudgeted = categories.reduce((s, c) => s + c.budgeted + (rollovers[c.name] || 0), 0);
  const totalSpent    = transactions.length > 0
    ? filteredTxns.filter(t => isExpense(t)).reduce((s, t) => s + t.amount, 0)
    : categories.reduce((s, c) => s + (c.spent || 0), 0);
  const overBudget    = categories.filter(c => getCategorySpent(c.name) > c.budgeted + (rollovers[c.name] || 0));

  const chartData = categories.map(c => {
    const effectiveBudgeted = c.budgeted + (rollovers[c.name] || 0);
    return {
      name:     c.name.split(' ')[0],
      budgeted: effectiveBudgeted,
      spent:    getCategorySpent(c.name),
      over:     getCategorySpent(c.name) > effectiveBudgeted,
    };
  });

  const bucketBudgeted = { needs: 0, wants: 0, savings: 0 };
  categories.forEach(c => { const b = RULE_BUCKETS[c.name]; if (b) bucketBudgeted[b] += c.budgeted + (rollovers[c.name] || 0); });

  const BUCKETS = [
    {
      key: 'needs', label: 'Needs', pct: 50, typeKey: 'Need',
      color: { bg: 'bg-blue-900/30', border: 'border-blue-700/40', dot: 'bg-blue-500', text: 'text-blue-400', bar: 'bg-blue-500' },
      desc: "Essentials you can't easily cut: housing, groceries, utilities, transportation, healthcare, and insurance.",
      cats: ['Housing', 'Utilities', 'Groceries', 'Transportation', 'Insurance', 'Healthcare', 'Minimum Debt Payments'],
    },
    {
      key: 'wants', label: 'Wants', pct: 30, typeKey: 'Want',
      color: { bg: 'bg-purple-900/30', border: 'border-purple-700/40', dot: 'bg-purple-500', text: 'text-purple-400', bar: 'bg-purple-500' },
      desc: 'Things that improve your life but you could live without.',
      cats: ['Dining Out', 'Entertainment', 'Shopping', 'Subscriptions', 'Personal Care', 'Travel', 'Fitness', 'Hobbies'],
    },
    {
      key: 'savings', label: 'Savings & Investing', pct: 20, typeKey: 'Savings',
      color: { bg: 'bg-emerald-900/30', border: 'border-emerald-700/40', dot: 'bg-emerald-500', text: 'text-emerald-400', bar: 'bg-emerald-500' },
      desc: 'Paying your future self: emergency fund, retirement, investments, and extra debt paydown.',
      cats: ['Emergency Fund', 'Retirement', 'Investments', 'Extra Debt Paydown', 'Education Fund'],
    },
  ];

  // ── Bills derivations ─────────────────────────────────────────────────
  const thisMonth = useMemo(() => currentMonthKey(), []);

  const bills = useMemo(() => budgetData.bills || [], [budgetData.bills]);

  const billsThisMonth = useMemo(() => {
    const now2   = new Date();
    const today  = new Date(now2.getFullYear(), now2.getMonth(), now2.getDate());
    const in7    = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    return bills
      .map(bill => {
        const dueDate   = billDueDateForMonth(bill, thisMonth);
        const isPaid    = bill.paidMonths?.[thisMonth] === true;
        const isOverdue = !isPaid && dueDate < today;
        const isDueSoon = !isPaid && !isOverdue && dueDate <= in7;
        return { ...bill, dueDate, isPaid, isOverdue, isDueSoon };
      })
      .sort((a, b) => a.dueDate - b.dueDate);
  }, [bills, thisMonth]);

  const billsSummary = useMemo(() => {
    const total    = billsThisMonth.reduce((s, b) => s + b.amount, 0);
    const paid     = billsThisMonth.filter(b => b.isPaid).reduce((s, b) => s + b.amount, 0);
    const paidCnt  = billsThisMonth.filter(b => b.isPaid).length;
    return { total, paid, paidCnt, count: billsThisMonth.length };
  }, [billsThisMonth]);

  // ── Month in Review derivations ───────────────────────────────────────
  // monthTxns: all transactions in the current calendar month (not period-filtered)
  const monthTxns = useMemo(() => {
    const now2   = new Date();
    const mStart = new Date(now2.getFullYear(), now2.getMonth(), 1);
    const mEnd   = new Date(now2.getFullYear(), now2.getMonth() + 1, 0, 23, 59, 59, 999);
    return transactions.filter(t => {
      const d = new Date(t.date + 'T12:00:00');
      return d >= mStart && d <= mEnd;
    });
  }, [transactions]);

  const mirData = useMemo(() => {
    const expenses            = monthTxns.filter(t => isExpense(t));
    const incomes             = monthTxns.filter(t => t.kind === 'income');
    const totalIncomeReceived = incomes.reduce((s, t) => s + t.amount, 0);
    const totalSpentMonth     = expenses.reduce((s, t) => s + t.amount, 0);
    const totalSaved          = totalIncomeReceived - totalSpentMonth;
    const savingsRate         = totalIncomeReceived > 0 ? (totalSaved / totalIncomeReceived) * 100 : null;
    const txnCount            = monthTxns.length;
    const mostExpensive       = expenses.length > 0
      ? expenses.reduce((max, t) => t.amount > max.amount ? t : max, expenses[0])
      : null;

    return { totalIncomeReceived, totalSpentMonth, totalSaved, savingsRate, txnCount, mostExpensive };
  }, [monthTxns]);

  // ── Coach message state ─────────────────────────────────────────────────
  const coachState = useMemo(() => {
    if (categories.length === 0 && transactions.length === 0) return 'fresh';
    if (transactions.length === 0 || totalBudgeted <= 0) return 'noData';
    const ratio = totalSpent / totalBudgeted;
    if (ratio > 1.0)  return 'over';
    if (ratio > 0.88) return 'close';
    return 'under';
  }, [categories.length, transactions.length, totalSpent, totalBudgeted]);

  const coachAmountLeft = totalBudgeted > 0
    ? fmt(Math.max(0, totalBudgeted - totalSpent))
    : income > 0 ? fmt(Math.max(0, income - totalSpent)) : '$0';

  // ── Savings total (for milestone celebrations) ──────────────────────────
  const savingsTotal = useMemo(() =>
    transactions
      .filter(t => isExpense(t) && (t.type === 'Savings' || t.category === 'Savings' || t.category === 'Investments'))
      .reduce((s, t) => s + t.amount, 0),
  [transactions]);

  // ── 7-day streak detection ───────────────────────────────────────────────
  const has7DayStreak = useMemo(() => {
    if (transactions.length < 7) return false;
    const txnDates = new Set(transactions.map(t => t.date));
    for (let i = 0; i < 7; i++) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      if (!txnDates.has(localDateString(d))) return false;
    }
    return true;
  }, [transactions]);

  const streakWeekKey = useMemo(() => {
    const d   = new Date();
    const jan1 = new Date(d.getFullYear(), 0, 1);
    const wk  = Math.ceil(((d - jan1) / 86400000 + jan1.getDay() + 1) / 7);
    return `streak_${d.getFullYear()}_W${wk}`;
  }, []);

  // ── Streak counter (consecutive days with ≥ 1 transaction) ────────────
  // Starts from today if logged; otherwise starts from yesterday (today is not yet over).
  const streakDays = useMemo(() => {
    if (transactions.length === 0) return 0;
    const txnDates = new Set(transactions.map(t => t.date));
    const todayStr = localDateString();
    const startOffset = txnDates.has(todayStr) ? 0 : 1;
    let count = 0;
    for (let i = startOffset; i < 366; i++) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      if (txnDates.has(localDateString(d))) {
        count++;
      } else {
        break;
      }
    }
    return count;
  }, [transactions]);

  // ── Smart Nudges ───────────────────────────────────────────────────────
  const smartNudges = useMemo(() => {
    if (period !== 'month') return [];
    const now2        = new Date();
    const daysInMonth = new Date(now2.getFullYear(), now2.getMonth() + 1, 0).getDate();
    const daysPassed  = now2.getDate();
    const monthPct    = daysPassed / daysInMonth;
    const nudges      = [];

    // 1. Overpace per category
    categories.forEach(cat => {
      const spent    = getCategorySpent(cat.name);
      if (spent <= 0) return;
      const budgeted = cat.budgeted + (rollovers[cat.name] || 0);
      if (budgeted <= 0) return;
      const spendPct = spent / budgeted;
      if (spendPct > monthPct && spendPct > 0.5) {
        const pct      = Math.round(spendPct * 100);
        const daysLeft = daysInMonth - daysPassed;
        nudges.push({
          id:       `overpace_${cat.name}`,
          priority: 1,
          icon:     '⚡',
          color:    'amber',
          text:     `Heads up — you've spent ${pct}% of your ${cat.name} budget with ${daysLeft} day${daysLeft !== 1 ? 's' : ''} left this month.`,
        });
      }
    });

    // 2. Under-budget streak (uses monthlySnapshots)
    const snapshots   = budgetData.monthlySnapshots || {};
    const sortedMonths = Object.keys(snapshots).sort().reverse();
    categories.forEach(cat => {
      if (getCategorySpent(cat.name) <= 0) return;
      let streak = 0;
      for (const mk of sortedMonths) {
        const snap = snapshots[mk]?.[cat.name];
        if (!snap) break;
        if (snap.spent < snap.effectiveBudget) streak++;
        else break;
      }
      if (streak >= 2) {
        nudges.push({
          id:       `streak_${cat.name}`,
          priority: 3,
          icon:     '🎉',
          color:    'emerald',
          text:     `Nice work — you've been under budget in ${cat.name} for ${streak} month${streak !== 1 ? 's' : ''} in a row.`,
        });
      }
    });

    // 3. Month-over-month spending spike (≥15%)
    const prevMK = (() => {
      const d = new Date(now2.getFullYear(), now2.getMonth() - 1, 1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    })();
    const lastSnap = snapshots[prevMK];
    if (lastSnap) {
      categories.forEach(cat => {
        const spentNow  = getCategorySpent(cat.name);
        if (spentNow <= 0) return;
        const spentLast = lastSnap[cat.name]?.spent || 0;
        if (spentLast <= 0) return;
        const changePct = ((spentNow - spentLast) / spentLast) * 100;
        if (changePct >= 15) {
          nudges.push({
            id:       `mom_${cat.name}`,
            priority: 2,
            icon:     '📈',
            color:    'rose',
            text:     `Your ${cat.name} spending is ${Math.round(changePct)}% higher than last month (${fmt(spentLast)} → ${fmt(spentNow)}).`,
          });
        }
      });
    }

    // 4. Unpaid bills due in 7 days or overdue
    const urgentBills = billsThisMonth.filter(b => (b.isDueSoon || b.isOverdue) && !b.isPaid);
    if (urgentBills.length > 0) {
      const urgentTotal = urgentBills.reduce((s, b) => s + b.amount, 0);
      nudges.push({
        id:       'unpaid_bills',
        priority: 0,
        icon:     '🔔',
        color:    'amber',
        text:     `You have ${urgentBills.length} unpaid bill${urgentBills.length !== 1 ? 's' : ''} due in the next 7 days totaling ${fmt(urgentTotal)}.`,
      });
    }

    return nudges
      .filter(n => !dismissedNudges.includes(n.id))
      .sort((a, b) => a.priority - b.priority)
      .slice(0, 4);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, categories, budgetData.monthlySnapshots, billsThisMonth, dismissedNudges,
      transactions, txnSpentByCategory, rollovers]);

  // ── Mutations ─────────────────────────────────────────────────────────
  const saveBudget = (newBudget) => updateSection('budget', newBudget);

  // ── Auto-normalize & recategorize ───────────────────────────────────────────
  // Runs whenever transactions change. Fixes data quality issues in stored txns:
  // 1. kind:"actual" or missing → "expense"  (old import formats)
  // 2. kind:"income" on a known expense category → "expense"  (misclassified txns)
  // 3. type:"expense" or invalid type → correct Need/Want/Savings
  // 4. Uncategorized expenses → keyword match + PLAID_TO_BUDGET fallback
  // 5. reviewed:false on a categorized expense → reviewed:true  (pre-fix Plaid imports)
  useEffect(() => {
    if (transactions.length === 0) return;

    let changed = false;
    const updated = transactions.map(t => {
      let next = t;

      // ── 1. Normalise kind ─────────────────────────────────────────────────
      // "actual" was an old alias; undefined/null defaults to "expense" since
      // income transactions are always explicitly marked kind:"income".
      if (next.kind === 'actual' || !next.kind) {
        next = { ...next, kind: 'expense' };
        changed = true;
      }

      // ── 2. Fix income misclassification ───────────────────────────────────
      // Three signals that a kind:"income" transaction is actually an expense:
      //   a) It came from Plaid (plaidTxnId). plaidSync filters credits
      //      (amount ≤ 0) before storing, so anything that passed is a debit.
      //   b) It already has a category in a spending bucket (Housing, etc.).
      //   c) Keyword engine matches the description to an expense category —
      //      catches manually-entered expenses logged as income by mistake
      //      (e.g. description "rent" entered with kind:"income").
      if (next.kind === 'income') {
        const isPlaid      = !!next.plaidTxnId;
        const hasExpenseCat = !!(next.category && RULE_BUCKETS[next.category]);
        const kwFlip       = (!isPlaid && !hasExpenseCat)
          ? categorizeTransaction(next.description, categoryRules)
          : null;
        if (isPlaid || hasExpenseCat || kwFlip) {
          const stdCat  = kwFlip?.category || null;
          const stdType = kwFlip?.type     || null;
          const cat  = stdCat ? mapToUserCategory(stdCat, stdType, categories) : next.category;
          const type = stdType || next.type || null;
          next = { ...next, kind: 'expense', reviewed: !!cat,
                   ...(cat  ? { category: cat  } : {}),
                   ...(type ? { type            } : {}),
          };
          changed = true;
        }
      }

      // ── 3. Fix swapped type (type:"expense" is not a valid Need/Want/Savings) ──
      if (next.type === 'expense' || (isExpense(next) && next.type && !['Need', 'Want', 'Savings'].includes(next.type))) {
        const userCat = categories.find(c => c.name === next.category);
        const stdCat  = STANDARD_CATEGORIES.find(s => s.name === next.category);
        const correctType = userCat?.type || stdCat?.type || null;
        if (correctType) { next = { ...next, type: correctType }; changed = true; }
      }

      // ── 4. Categorize uncategorized expenses ──────────────────────────────
      if (!next.category && isExpense(next)) {
        const kwMatch = categorizeTransaction(next.description, categoryRules);
        const plaidFallback = !kwMatch && next.plaidCategory ? (PLAID_TO_BUDGET[next.plaidCategory] || null) : null;
        const stdCat  = kwMatch?.category || plaidFallback?.category || null;
        const stdType = kwMatch?.type     || plaidFallback?.type
                        || (stdCat && STANDARD_CATEGORIES.find(s => s.name === stdCat)?.type)
                        || null;
        if (stdCat) {
          const cat  = mapToUserCategory(stdCat, stdType, categories);
          const type = stdType || (categories.find(c => c.name === cat)?.type) || null;
          next = { ...next, category: cat, type, reviewed: true };
          changed = true;
        }
      }

      // ── 5. Stamp reviewed:true for any categorized expense ────────────────
      // Fixes Plaid transactions imported before the plaidSync.js fix that still
      // carry reviewed:false even though they were successfully auto-categorized.
      if (isExpense(next) && next.category && !next.reviewed) {
        next = { ...next, reviewed: true };
        changed = true;
      }

      return next;
    });

    if (changed) saveBudget({ ...budgetData, transactions: updated });
  }, [transactions]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── One-time migration to standard categories ────────────────────────────
  // Runs once per session after data loads. Renames old category names to new
  // standard names, re-categorizes all unreviewed transactions with updated
  // keyword rules, and deletes categories that have zero budget AND no transactions.
  const migrateRef = useRef(false);
  useEffect(() => {
    if (migrateRef.current || dataLoading) return;
    if (!categories.length && !transactions.length) return;
    migrateRef.current = true;

    // 1. Rename categories per migration map
    const txnCounts = {};
    transactions.forEach(t => {
      if (t.category) txnCounts[t.category] = (txnCounts[t.category] || 0) + 1;
    });

    let catChanged = false;
    const migratedCats = categories
      .map(c => {
        const newName = CATEGORY_MIGRATION[c.name];
        if (newName) { catChanged = true; return { ...c, name: newName, icon: STANDARD_CATEGORIES.find(s => s.name === newName)?.icon || c.icon, type: STANDARD_CATEGORIES.find(s => s.name === newName)?.type || c.type }; }
        // Update icon/type if it's a known standard category that may have a stale icon
        const std = STANDARD_CATEGORIES.find(s => s.name === c.name);
        if (std && (std.icon !== c.icon || std.type !== c.type)) { catChanged = true; return { ...c, icon: std.icon, type: std.type }; }
        return c;
      })
      .filter(c => {
        // Delete categories with no budget AND no transactions (old/empty leftovers)
        const hasTransactions = (txnCounts[c.name] || 0) > 0;
        const hasBudget = (c.budgeted || 0) > 0;
        if (!hasTransactions && !hasBudget) { catChanged = true; return false; }
        return true;
      });

    // Build reverse migration map for renaming transaction categories
    const txnRename = {};
    Object.entries(CATEGORY_MIGRATION).forEach(([oldName, newName]) => { txnRename[oldName] = newName; });

    // 2. Re-run keyword engine on all unreviewed transactions + rename migrated categories
    let txnChanged = false;
    const migratedTxns = transactions.map(t => {
      if (!isExpense(t)) return t;
      // Rename if category was migrated
      const renamedCat = t.category ? (txnRename[t.category] || t.category) : t.category;
      if (t.reviewed) {
        if (renamedCat !== t.category) { txnChanged = true; return { ...t, category: renamedCat }; }
        return t;
      }
      // Re-categorize unreviewed with latest keyword rules
      const kwMatch = categorizeTransaction(t.description, categoryRules);
      const plaidFallback = !kwMatch && t.plaidCategory ? (PLAID_TO_BUDGET[t.plaidCategory] || null) : null;
      const stdCat  = kwMatch?.category || plaidFallback?.category || renamedCat || null;
      const stdType = kwMatch?.type     || plaidFallback?.type     || t.type     || null;
      const cat  = stdCat ? mapToUserCategory(stdCat, stdType, migratedCats) : null;
      const type = stdType
                   || (cat && migratedCats.find(c => c.name === cat)?.type)
                   || (cat && STANDARD_CATEGORIES.find(s => s.name === cat)?.type)
                   || null;
      if (cat !== t.category || type !== t.type) { txnChanged = true; return { ...t, category: cat, type }; }
      return t;
    });

    if (catChanged || txnChanged) {
      saveBudget({ ...budgetData, categories: migratedCats, transactions: migratedTxns });
    }
  }, [dataLoading, categories, transactions]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Rollover snapshot: runs once data loads and whenever the month flips ──
  // When a new month begins, we snapshot the previous month's spending, compute
  // how much each rollover-enabled category had left over, and carry that forward.
  useEffect(() => {
    if (dataLoading || !categories.length) return;
    const thisMonth = currentMonthKey();
    if (budgetData.lastSnapshotMonth === thisMonth) return;

    const prevMonth = budgetData.lastSnapshotMonth;
    let newRollovers = {};
    let snapshot     = {};

    if (prevMonth) {
      // Calculate spending from ALL transactions that fall in the previous month
      const [py, pm] = prevMonth.split('-').map(Number);
      const prevStart = new Date(py, pm - 1, 1);
      const prevEnd   = new Date(py, pm, 0, 23, 59, 59, 999);

      const prevSpent = {};
      transactions.forEach(t => {
        if (isExpense(t) && t.category) {
          const d = new Date(t.date + 'T12:00:00');
          if (d >= prevStart && d <= prevEnd) {
            prevSpent[t.category] = (prevSpent[t.category] || 0) + t.amount;
          }
        }
      });

      categories.forEach(cat => {
        const rollIn      = rollovers[cat.name] || 0;
        const effective   = cat.budgeted + rollIn;
        const spentPrev   = prevSpent[cat.name] || 0;
        const rollOut     = rolloverEnabled[cat.name] ? Math.max(0, effective - spentPrev) : 0;
        snapshot[cat.name] = { budgeted: cat.budgeted, rolloverIn: rollIn, effectiveBudget: effective, spent: spentPrev, rolloverOut: rollOut };
        if (rolloverEnabled[cat.name]) newRollovers[cat.name] = rollOut;
      });
    }

    saveBudget({
      ...budgetData,
      lastSnapshotMonth: thisMonth,
      monthlySnapshots: prevMonth
        ? { ...(budgetData.monthlySnapshots || {}), [prevMonth]: snapshot }
        : (budgetData.monthlySnapshots || {}),
      rollovers: prevMonth ? newRollovers : {},
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoading, categories.length, budgetData.lastSnapshotMonth]);

  // ── Celebration trigger: first transaction ever ─────────────────────────
  useEffect(() => {
    if (dataLoading) return;
    if (!mountedRef.current) {
      // First run after data loads — establish baseline without firing
      mountedRef.current = true;
      prevTxnLen.current = transactions.length;
      return;
    }
    const prev = prevTxnLen.current;
    prevTxnLen.current = transactions.length;
    if (prev === 0 && transactions.length >= 1 && !getCelebrated('firstTransaction')) {
      fireCelebration('🎉', 'Great start!', 'Your first step toward financial clarity!');
      markCelebrated('firstTransaction');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions.length, dataLoading]);

  // ── Celebration trigger: savings milestones ─────────────────────────────
  useEffect(() => {
    if (dataLoading) return;
    if (prevSavingsRef.current === null) { prevSavingsRef.current = savingsTotal; return; }
    const prev = prevSavingsRef.current;
    prevSavingsRef.current = savingsTotal;
    if (savingsTotal <= prev) return;
    for (const m of [500, 1000, 2000, 5000, 10000]) {
      if (prev < m && savingsTotal >= m && !getCelebrated(`sav_${m}`)) {
        fireCelebration('💰', `$${m.toLocaleString()} saved milestone!`, "Every dollar saved is a brick in your financial foundation. 🏗️");
        markCelebrated(`sav_${m}`);
        break;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savingsTotal, dataLoading]);

  // ── Celebration trigger: 7-day streak ──────────────────────────────────
  useEffect(() => {
    if (dataLoading || !has7DayStreak) return;
    if (!getCelebrated(streakWeekKey)) {
      fireCelebration('🔥', '7-day streak!', "You've logged expenses every day this week. Consistency is everything.");
      markCelebrated(streakWeekKey);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [has7DayStreak, dataLoading, streakWeekKey]);

  // ── Celebration trigger: streak milestones (14 / 30 / 60 / 90 days) ───
  useEffect(() => {
    if (dataLoading || streakDays < 14) return;
    const STREAK_MILESTONE_MSGS = {
      90: { icon: '🏆', msg: '90-day streak!',     sub: "Three months of daily tracking. You've transformed your relationship with money. 🌟" },
      60: { icon: '🌟', msg: '60-day streak!',     sub: "Two months strong. Financial awareness is now part of who you are." },
      30: { icon: '⭐', msg: '30-day streak!',     sub: "A full month! You're in the top tier of people who track their spending consistently." },
      14: { icon: '🔥', msg: '14-day streak!',     sub: "Two weeks of consistent tracking. You're building a real financial habit!" },
    };
    for (const days of [90, 60, 30, 14]) {
      if (streakDays >= days && !getCelebrated(`streak_milestone_${days}`)) {
        const { icon, msg, sub } = STREAK_MILESTONE_MSGS[days];
        fireCelebration(icon, msg, sub);
        markCelebrated(`streak_milestone_${days}`);
        break;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streakDays, dataLoading]);

  const handleToggleRollover = (catName, enabled) => {
    saveBudget({
      ...budgetData,
      rolloverEnabled: { ...rolloverEnabled, [catName]: enabled },
    });
  };

  const handleAddTransaction = (txn) => {
    saveBudget({ ...budgetData, transactions: [...transactions, txn] });
  };

  const handleAddTransactionBatch = (txns) => {
    saveBudget({ ...budgetData, transactions: [...transactions, ...txns] });
  };

  const handleDeleteTransaction = (id) => {
    saveBudget({ ...budgetData, transactions: transactions.filter(t => t.id !== id) });
  };

  const handleSyncPlaid = async () => {
    if (syncingPlaid) return;
    setSyncingPlaid(true);
    try {
      const d90Ago = new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10);
      const today  = localDateString();
      const { transactions: rawTxns = [] } = await fetchPlaidTransactions(d90Ago, today);

      const existingPlaidIds = new Set(transactions.map(t => t.plaidTxnId).filter(Boolean));
      const newTxns = rawTxns
        .filter(txn => !txn.pending && txn.amount > 0 && !existingPlaidIds.has(txn.transaction_id))
        .map(txn => {
          const desc          = txn.merchant_name || txn.name || '';
          const match         = categorizeTransaction(desc, categoryRules);
          const plaidFallback = !match ? (PLAID_TO_BUDGET[txn.category] || null) : null;
          const stdCat  = match?.category || plaidFallback?.category || null;
          const stdType = match?.type     || plaidFallback?.type     || null;
          const cat     = stdCat ? mapToUserCategory(stdCat, stdType, categories) : null;
          const type    = stdType
                          || (cat && categories.find(c => c.name === cat)?.type)
                          || null;
          return {
            id:           `plaid_${txn.transaction_id}`,
            plaidTxnId:   txn.transaction_id,
            plaidCategory: txn.category || null,
            date:         txn.date,
            amount:       Math.abs(txn.amount),
            description:  desc,
            category:     cat,
            type:         type,
            kind:         'expense',
            source:       match ? 'auto' : (plaidFallback ? 'auto' : 'plaid'),
            reviewed:     !!cat,
          };
        });

      if (newTxns.length > 0) {
        saveBudget({ ...budgetData, transactions: [...transactions, ...newTxns] });
      }
      setLastPlaidSync(new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }));
    } catch (err) {
      console.error('[Budget] Plaid sync failed:', err);
    } finally {
      setSyncingPlaid(false);
    }
  };

  const handleReviewTransaction = (txnId, { category, type, remember }) => {
    const newRules = remember ? (() => {
      const txn = transactions.find(t => t.id === txnId);
      if (!txn) return categoryRules;
      const key = txn.description.toLowerCase().trim().split(/\s+/).slice(0, 3).join(' ');
      return { ...categoryRules, [key]: { category, type } };
    })() : categoryRules;

    const newTxns = transactions.map(t =>
      t.id === txnId ? { ...t, category, type, reviewed: true, source: 'manual' } : t
    );
    saveBudget({ ...budgetData, transactions: newTxns, categoryRules: newRules });
  };

  // Creates a transaction instead of updating cat.spent directly
  const handleLogSpending = (catName, amount) => {
    const bucket    = RULE_BUCKETS[catName];
    const inferType = bucket === 'needs' ? 'Need' : bucket === 'savings' ? 'Savings' : 'Want';
    const catInfo   = ALL_CATEGORIES.find(c => c.name === catName);
    saveBudget({
      ...budgetData,
      transactions: [
        ...transactions,
        {
          id: makeTxnId(),
          date: localDateString(), // local date, not UTC
          amount,
          description: `${catName} expense`,
          category: catName,
          type: catInfo?.type || inferType,
          kind: 'expense',
          source: 'manual',
          reviewed: true,
        },
      ],
    });
  };

  const handleSetGoalLink = (catName, goalId) => {
    const updated = { ...categoryGoalLinks };
    if (goalId) updated[catName] = goalId;
    else delete updated[catName];
    saveBudget({ ...budgetData, categoryGoalLinks: updated });
  };

  const handleAddCategory = (cat) => saveBudget({ ...budgetData, categories: [...categories, cat] });

  const handleDeleteCategory = (name) => {
    saveBudget({ ...budgetData, categories: categories.filter(c => c.name !== name) });
  };

  const handleSaveBudgetAmt = (catName, newBudgeted) => {
    saveBudget({
      ...budgetData,
      categories: categories.map(c => c.name === catName ? { ...c, budgeted: newBudgeted } : c),
    });
  };

  const handleSaveIncome = () => {
    const val = parseInt(incomeInput.replace(/[^0-9]/g, ''), 10);
    if (!isNaN(val) && val > 0) saveBudget({ ...budgetData, income: val });
    setEditingIncome(false);
  };

  // ── Bills handlers ─────────────────────────────────────────────────────
  const handleAddBill = (billData) => {
    saveBudget({ ...budgetData, bills: [...bills, { ...billData, id: makeBillId(), paidMonths: {} }] });
  };

  const handleDeleteBill = (id) => {
    saveBudget({ ...budgetData, bills: bills.filter(b => b.id !== id) });
  };

  const handleToggleBillPaid = (id) => {
    const bill     = bills.find(b => b.id === id);
    const wasUnpaid = bill && !bill.paidMonths?.[thisMonth];
    const updatedBills = bills.map(b => {
      if (b.id !== id) return b;
      const pm = { ...(b.paidMonths || {}) };
      if (pm[thisMonth]) delete pm[thisMonth];
      else pm[thisMonth] = true;
      return { ...b, paidMonths: pm };
    });
    saveBudget({ ...budgetData, bills: updatedBills });
    if (wasUnpaid && bill) {
      fireCelebration('✅', `${bill.name} — paid!`, "One less thing to worry about. You're on top of it. 💪");
    }
  };

  const dismissNudge = (id) => setDismissedNudges(prev => [...prev, id]);

  // ── Render ─────────────────────────────────────────────────────────────
  const now = new Date();
  const periodLabel = period === 'week'
    ? 'This Week'
    : period === 'last30'
      ? 'Last 30 Days'
      : period === 'year'
        ? String(now.getFullYear())
        : now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {showAddCategory && (
        <AddCategoryModal onClose={() => setShowAddCategory(false)} onAdd={handleAddCategory} existingCategoryNames={categories.map(c => c.name)} />
      )}
      {showAddTxn && (
        <AddTransactionModal
          onClose={() => setShowAddTxn(false)}
          onAdd={handleAddTransaction}
          onAddMultiple={handleAddTransactionBatch}
          userCategories={categories}
          defaultKind={addTxnKind}
          customRules={categoryRules}
        />
      )}
      {showAddBill && (
        <AddBillModal onClose={() => setShowAddBill(false)} onAdd={handleAddBill} />
      )}
      {/* Celebration overlay — fixed, rendered outside page flow */}
      {celebration && (
        <CelebrationOverlay
          celebration={celebration}
          onDone={() => setCelebration(null)}
          onDisableAll={disableCelebrations}
        />
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Budget</h1>
          <p className="text-slate-400 text-sm mt-0.5">{periodLabel} · Click any category to adjust your goal</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {(linkedAccountsData || []).some(a => a.connectionType === 'plaid') && (
            userProfile?.plan === 'free' ? (
              <div
                title="Automatic bank sync is a Pro feature — upgrade to Pro to enable"
                className="flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg border bg-slate-700/40 border-slate-600/60 text-slate-500 cursor-not-allowed select-none"
              >
                <Lock size={13} />
                Upgrade to Pro
              </div>
            ) : (
              <button
                onClick={handleSyncPlaid}
                disabled={syncingPlaid}
                title={lastPlaidSync ? `Last synced ${lastPlaidSync}` : 'Sync transactions from Plaid'}
                className={`flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg border transition-colors ${
                  syncingPlaid
                    ? 'bg-slate-700/50 border-slate-600 text-slate-500 cursor-not-allowed'
                    : 'bg-slate-700 hover:bg-slate-600 border-slate-600 text-slate-200'
                }`}
              >
                <RefreshCw size={14} className={syncingPlaid ? 'animate-spin' : ''} />
                {syncingPlaid ? 'Syncing…' : 'Sync Plaid'}
              </button>
            )
          )}
          <button
            onClick={() => { setAddTxnKind('expense'); setShowAddTxn(true); }}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-rose-600 hover:bg-rose-500 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            <ArrowUpCircle size={15} /> Expense
          </button>
          <button
            onClick={() => { setAddTxnKind('income'); setShowAddTxn(true); }}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            <ArrowDownCircle size={15} /> Income
          </button>
          <button
            onClick={() => setShowAddCategory(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            <Plus size={15} /> Category
          </button>
        </div>
      </div>

      {/* Period tabs + streak badge */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1 bg-slate-800/60 border border-slate-700 rounded-xl p-1">
          {[['week', 'This Week'], ['last30', 'Last 30 Days'], ['month', 'This Month'], ['year', 'This Year']].map(([p, l]) => (
            <button key={p} onClick={() => setPeriod(p)}
              className={`text-sm px-4 py-1.5 rounded-lg font-medium transition-colors ${
                period === p ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-slate-200'
              }`}
            >{l}</button>
          ))}
        </div>
        <StreakBadge streakDays={streakDays} />
      </div>

      {/* Smart Nudges */}
      <SmartNudgesPanel nudges={smartNudges} onDismiss={dismissNudge} />

      {/* Coach message */}
      {!coachDismissed && (
        <CoachMessage
          state={coachState}
          amountLeft={coachAmountLeft}
          onDismiss={() => setCoachDismissed(true)}
        />
      )}

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Income — editable */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Monthly Income</div>
          {editingIncome ? (
            <div className="flex items-center gap-1.5">
              <span className="text-slate-400 text-sm">$</span>
              <input type="number" value={incomeInput} onChange={e => setIncomeInput(e.target.value)} autoFocus min="0"
                onKeyDown={e => { if (e.key === 'Enter') handleSaveIncome(); if (e.key === 'Escape') setEditingIncome(false); }}
                className="w-full bg-slate-700 border border-blue-500 rounded px-2 py-1 text-slate-100 text-sm focus:outline-none tabular-nums"
              />
              <button onClick={handleSaveIncome} className="p-1 text-emerald-400 hover:text-emerald-300"><Check size={14} /></button>
              <button onClick={() => setEditingIncome(false)} className="p-1 text-slate-400 hover:text-slate-300"><X size={14} /></button>
            </div>
          ) : (
            <button onClick={() => { setIncomeInput(String(income)); setEditingIncome(true); }}
              className="flex items-center gap-2 group">
              <div className={`text-xl font-bold ${income > 0 ? 'text-emerald-400' : 'text-slate-500'}`}>
                {income > 0 ? fmt(income) : 'Set income'}
              </div>
              <Pencil size={12} className="text-slate-600 group-hover:text-slate-400 transition-colors" />
            </button>
          )}
        </div>

        {[
          { label: 'Total Budgeted', value: fmt(totalBudgeted), color: 'text-blue-400' },
          { label: 'Total Spent',    value: fmt(totalSpent),    color: totalSpent > totalBudgeted ? 'text-rose-400' : 'text-slate-200' },
          {
            label: 'Remaining',
            value: income > 0 ? fmt(income - totalSpent) : fmt(totalBudgeted - totalSpent),
            color: (income > 0 ? income - totalSpent : totalBudgeted - totalSpent) >= 0 ? 'text-emerald-400' : 'text-rose-400',
          },
        ].map(k => (
          <div key={k.label} className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">{k.label}</div>
            <div className={`text-xl font-bold ${k.color}`}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Over-budget warning */}
      {overBudget.length > 0 && (
        <div className="bg-rose-950/50 border border-rose-700/50 rounded-xl px-4 py-3 flex items-center gap-3">
          <span className="text-rose-400 text-sm font-semibold">
            ⚠ {overBudget.length} {overBudget.length === 1 ? 'category' : 'categories'} over budget
          </span>
          <span className="text-rose-300/70 text-sm">·</span>
          <span className="text-rose-300/70 text-sm">{overBudget.map(c => c.name).join(', ')}</span>
        </div>
      )}

      {/* Needs Review panel */}
      {uncategorizedTxns.length > 0 && (
        <NeedsReviewPanel
          transactions={uncategorizedTxns}
          userCategories={categories}
          onReview={handleReviewTransaction}
        />
      )}

      {/* Empty state */}
      {categories.length === 0 && transactions.length === 0 ? (
        <EmptyState
          icon="📊"
          title="No budget set up yet"
          description="Add budget categories to track your spending, or log a transaction to get started."
          actionLabel="Add Category"
          onAction={() => setShowAddCategory(true)}
          secondaryLabel={income === 0 ? 'Set Income First' : undefined}
          onSecondary={income === 0 ? () => { setIncomeInput(''); setEditingIncome(true); } : undefined}
        />
      ) : (
        <>
          {/* Categories + Chart */}
          {categories.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              {/* Category list */}
              <div className="lg:col-span-3 bg-slate-800 border border-slate-700 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-slate-200 text-base">Categories</h2>
                  <span className="text-xs text-slate-500 flex items-center gap-1.5">
                    <Pencil size={10} /> Click row to edit budget
                  </span>
                </div>
                <div>
                  {categories.map(cat => (
                    <CategoryRow
                      key={cat.name}
                      cat={cat}
                      spent={getCategorySpent(cat.name)}
                      income={income}
                      onSaveBudget={(val) => handleSaveBudgetAmt(cat.name, val)}
                      onLogSpending={(amt) => handleLogSpending(cat.name, amt)}
                      onDelete={() => handleDeleteCategory(cat.name)}
                      rollover={rollovers[cat.name] || 0}
                      rolloverEnabled={rolloverEnabled[cat.name] || false}
                      onToggleRollover={(enabled) => handleToggleRollover(cat.name, enabled)}
                      goals={goals}
                      goalLink={categoryGoalLinks[cat.name] || null}
                      onSetGoalLink={(goalId) => handleSetGoalLink(cat.name, goalId)}
                    />
                  ))}
                </div>
              </div>

              {/* Chart — bar for week/month, area for year */}
              <div className="lg:col-span-2 bg-slate-800 border border-slate-700 rounded-xl p-5">
                {period === 'year' ? (
                  <>
                    <h2 className="font-semibold text-slate-200 text-base mb-4">Monthly Spending ({now.getFullYear()})</h2>
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={monthlyChartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 11 }} />
                        <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickFormatter={v => `$${v >= 1000 ? (v/1000).toFixed(0)+'k' : v}`} />
                        <Tooltip
                          contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
                          formatter={(v) => [fmt(v), 'Spent']}
                          labelStyle={{ color: '#cbd5e1' }}
                        />
                        <Area type="monotone" dataKey="spent" stroke="#3b82f6" strokeWidth={2} fill="url(#spendGrad)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </>
                ) : (
                  <>
                    <h2 className="font-semibold text-slate-200 text-base mb-4">Budgeted vs Spent</h2>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={chartData} margin={{ top: 0, right: 0, left: -20, bottom: 50 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 10 }} angle={-45} textAnchor="end" />
                        <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickFormatter={v => `$${v >= 1000 ? (v/1000)+'k' : v}`} />
                        <Tooltip
                          contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
                          labelStyle={{ color: '#cbd5e1' }}
                          itemStyle={{ color: '#94a3b8' }}
                          formatter={(v) => fmt(v)}
                        />
                        <Bar dataKey="budgeted" name="Budgeted" fill="#3b82f6" opacity={0.4} radius={[3, 3, 0, 0]} />
                        <Bar dataKey="spent" name="Spent" radius={[3, 3, 0, 0]}>
                          {chartData.map((entry, index) => (
                            <Cell key={index} fill={entry.over ? '#ef4444' : '#10b981'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="flex items-center gap-4 mt-1 justify-center">
                      <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-blue-500/40 rounded-sm" /><span className="text-xs text-slate-400">Budgeted</span></div>
                      <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-emerald-500 rounded-sm" /><span className="text-xs text-slate-400">Under budget</span></div>
                      <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-rose-500 rounded-sm" /><span className="text-xs text-slate-400">Over budget</span></div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Transaction list */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-slate-200 text-base">Transactions</h2>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">{filteredTxns.length} for {periodLabel}</span>
                <button
                  onClick={() => { setAddTxnKind('expense'); setShowAddTxn(true); }}
                  className="flex items-center gap-1 text-xs bg-slate-700 hover:bg-slate-600 border border-slate-600 text-slate-300 px-2.5 py-1.5 rounded-lg transition-colors"
                >
                  <Plus size={11} /> Add
                </button>
              </div>
            </div>
            <TransactionList transactions={filteredTxns} onDelete={handleDeleteTransaction} onReview={handleReviewTransaction} userCategories={categories} />
          </div>

          {/* Bills Tracker */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
            {/* Header row */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Calendar size={16} className="text-slate-400" />
                <h2 className="font-semibold text-slate-200 text-base">Bills</h2>
                <span className="text-xs text-slate-500">
                  {now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </span>
              </div>
              <button
                onClick={() => setShowAddBill(true)}
                className="flex items-center gap-1 text-xs bg-slate-700 hover:bg-slate-600
                           border border-slate-600 text-slate-300 px-2.5 py-1.5 rounded-lg transition-colors"
              >
                <Plus size={11} /> Add Bill
              </button>
            </div>

            {/* Summary strip */}
            {billsThisMonth.length > 0 && (
              <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-slate-400 mb-4
                              px-3 py-2 bg-slate-700/30 rounded-lg">
                <span className="font-medium text-slate-300">
                  {billsSummary.paidCnt} of {billsSummary.count} paid
                </span>
                <span className="text-slate-600">·</span>
                <span>{fmt(billsSummary.total)} total</span>
                <span className="text-slate-600">·</span>
                <span className="text-emerald-400">{fmt(billsSummary.paid)} paid so far</span>
                {billsSummary.total - billsSummary.paid > 0 && (
                  <>
                    <span className="text-slate-600">·</span>
                    <span className="text-rose-400">{fmt(billsSummary.total - billsSummary.paid)} remaining</span>
                  </>
                )}
              </div>
            )}

            {/* Bill rows */}
            {billsThisMonth.length === 0 ? (
              <div className="py-8 text-center">
                <Bell size={28} className="text-slate-600 mx-auto mb-2" />
                <p className="text-slate-500 text-sm font-medium">No bills yet</p>
                <p className="text-slate-600 text-xs mt-1">
                  Click "Add Bill" to track rent, utilities, subscriptions, and more.
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                {billsThisMonth.map(bill => {
                  const dueDateStr = bill.dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  const rowBg = bill.isPaid
                    ? ''
                    : bill.isOverdue
                      ? 'bg-rose-950/30 border border-rose-700/30 rounded-lg'
                      : bill.isDueSoon
                        ? 'bg-amber-950/30 border border-amber-700/30 rounded-lg'
                        : '';
                  return (
                    <div key={bill.id}
                      className={`flex items-center gap-3 px-3 py-2.5 group transition-colors
                                  hover:bg-slate-700/30 rounded-lg ${rowBg}`}>
                      {/* Checkbox */}
                      <button
                        onClick={() => handleToggleBillPaid(bill.id)}
                        className={`w-5 h-5 rounded-md border-2 flex-shrink-0 flex items-center
                                    justify-center transition-colors ${
                          bill.isPaid
                            ? 'bg-emerald-600 border-emerald-600'
                            : bill.isOverdue
                              ? 'border-rose-500 hover:border-emerald-500'
                              : 'border-slate-500 hover:border-emerald-500'
                        }`}
                      >
                        {bill.isPaid && <Check size={11} className="text-white" />}
                      </button>

                      {/* Name + meta */}
                      <div className="flex-1 min-w-0">
                        <span className={`text-sm font-medium truncate block ${
                          bill.isPaid ? 'text-slate-500 line-through' : 'text-slate-200'
                        }`}>
                          {bill.name}
                        </span>
                        <span className="text-xs text-slate-500">
                          {bill.category && `${bill.category} · `}
                          {bill.frequency !== 'monthly' && (
                            <span className="capitalize">{bill.frequency} · </span>
                          )}
                          Due {dueDateStr}
                          {bill.isOverdue && !bill.isPaid && (
                            <span className="text-rose-400 font-medium"> · Overdue</span>
                          )}
                          {bill.isDueSoon && !bill.isPaid && !bill.isOverdue && (
                            <span className="text-amber-400 font-medium"> · Due soon</span>
                          )}
                        </span>
                      </div>

                      {/* Amount */}
                      <span className={`text-sm font-semibold tabular-nums flex-shrink-0 ${
                        bill.isPaid ? 'text-slate-500' : 'text-slate-200'
                      }`}>
                        {fmt(bill.amount)}
                      </span>

                      {/* Delete */}
                      <button
                        onClick={() => handleDeleteBill(bill.id)}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-rose-900/40 transition-all"
                        aria-label="Delete bill"
                      >
                        <Trash2 size={11} className="text-rose-400" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 50/30/20 Rule */}
          {income > 0 && (
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 space-y-5">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="font-semibold text-slate-200 text-base">The 50 / 30 / 20 Rule</h2>
                  <span className="text-xs text-slate-500 bg-slate-700/60 px-2 py-0.5 rounded-full">Framework</span>
                  {transactions.length > 0 && (
                    <span className="text-xs text-blue-400 bg-blue-900/20 border border-blue-700/30 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Zap size={9} /> Live from transactions
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Spend <span className="text-blue-400 font-medium">50%</span> on needs,{' '}
                  <span className="text-purple-400 font-medium">30%</span> on wants, and put{' '}
                  <span className="text-emerald-400 font-medium">20%</span> toward savings &amp; investing.
                  Based on your {fmt(income)}/mo income:
                </p>
              </div>

              {/* Needs-review warning — excluded from bucket totals */}
              {unreviewedExpenseCount > 0 && (
                <div className="flex items-center gap-3 px-4 py-3 bg-amber-950/50 border border-amber-700/50 rounded-xl text-xs">
                  <AlertTriangle size={14} className="text-amber-400 flex-shrink-0" />
                  <span className="text-amber-300">
                    <span className="font-semibold">{unreviewedExpenseCount} transaction{unreviewedExpenseCount !== 1 ? 's' : ''} still need{unreviewedExpenseCount === 1 ? 's' : ''} review</span>
                    {' '}— categorize them to see your full breakdown. Their amounts are not included in the totals below.
                  </span>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {BUCKETS.map(bucket => {
                  const target = income * (bucket.pct / 100);
                  // Use transaction-based actual if available, else budgeted amounts
                  const actual = transactions.length > 0
                    ? bucketActual[bucket.key]
                    : bucketBudgeted[bucket.key];
                  const diff   = actual - target;
                  const barPct = target > 0 ? Math.min((actual / target) * 100, 100) : 0;
                  const overT  = actual > target * 1.0;
                  const closeT = actual >= target * 0.8 && actual <= target;
                  const barColor = overT ? 'bg-rose-500' : closeT ? 'bg-amber-400' : bucket.color.bar;
                  const cl = bucket.color;

                  return (
                    <div key={bucket.key} className={`p-4 rounded-xl border ${cl.bg} ${cl.border}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${cl.dot}`} />
                          <span className={`text-sm font-semibold ${cl.text}`}>{bucket.label}</span>
                        </div>
                        <span className="text-xs text-slate-400 font-bold">{bucket.pct}%</span>
                      </div>
                      <div className="mb-0.5">
                        <span className="text-xl font-bold text-slate-100">{fmt(actual)}</span>
                        <span className="text-xs text-slate-500 ml-1">actual</span>
                      </div>
                      <div className="text-xs text-slate-500 mb-3">target {fmt(target)}/mo</div>
                      <div className="w-full bg-slate-700 rounded-full h-1.5 mb-2">
                        <div className={`h-1.5 rounded-full transition-all ${barColor}`} style={{ width: `${barPct}%` }} />
                      </div>
                      <div className={`text-xs font-semibold mb-3 ${overT ? 'text-rose-400' : closeT ? 'text-amber-400' : 'text-emerald-400'}`}>
                        {overT  ? `↑ ${fmt(Math.abs(diff))} over target`  : ''}
                        {!overT && diff < 0 ? `↓ ${fmt(Math.abs(diff))} under target` : ''}
                        {!overT && diff >= 0 && diff <= target * 0.0 ? '✓ On target' : ''}
                        {!overT && diff >= 0 && diff < target ? '✓ On track' : ''}
                      </div>
                      <p className="text-xs text-slate-400 leading-relaxed mb-3">{bucket.desc}</p>
                      <div className="space-y-1 pt-2 border-t border-slate-700/50">
                        {bucket.cats.map(name => {
                          const cat = categories.find(c => c.name === name);
                          return cat ? (
                            <div key={name} className="flex justify-between text-xs">
                              <span className="text-slate-500">{cat.icon} {name}</span>
                              <span className="text-slate-300 tabular-nums font-medium">{fmt(getCategorySpent(name))}</span>
                            </div>
                          ) : null;
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Category benchmarks table */}
              <div>
                <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                  Category Benchmarks
                  <span className="text-xs font-normal text-slate-500">· based on {fmt(income)}/mo income</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {categories.map(cat => {
                    const bench = BENCHMARKS[cat.name];
                    if (!bench) return null;
                    const suggested  = Math.round(income * bench.pct / 100);
                    const diff       = cat.budgeted - suggested;
                    const bucket     = RULE_BUCKETS[cat.name];
                    const bucketColors = { needs: 'text-blue-400 bg-blue-900/20', wants: 'text-purple-400 bg-purple-900/20', savings: 'text-emerald-400 bg-emerald-900/20' };
                    const bucketCl   = bucketColors[bucket] || 'text-slate-400 bg-slate-700/30';
                    return (
                      <div key={cat.name} className="flex items-center gap-3 p-2.5 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 transition-colors">
                        <span className="text-base w-6 flex-shrink-0 text-center">{cat.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-xs font-medium text-slate-300">{cat.name}</span>
                            {bucket && <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${bucketCl}`}>{bucket}</span>}
                          </div>
                          <div className="text-xs text-slate-500">{bench.range}</div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-xs font-bold text-slate-200 tabular-nums">{fmt(suggested)}</div>
                          <div className={`text-xs tabular-nums ${Math.abs(diff) < 50 ? 'text-emerald-400' : diff > 0 ? 'text-amber-400' : 'text-slate-500'}`}>
                            {Math.abs(diff) < 50 ? '✓ on target' : diff > 0 ? `+${fmt(diff)} over` : `${fmt(Math.abs(diff))} under`}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-blue-950/30 border border-blue-800/30 rounded-xl text-xs text-blue-300/80 leading-relaxed">
                <Info size={14} className="text-blue-400 flex-shrink-0 mt-0.5" />
                <span>
                  <strong className="text-blue-300">Pro tip:</strong> The 50/30/20 rule is a starting point, not a law.
                  High cost-of-living cities often push housing to 35–40%, which is fine — just compensate by trimming wants.
                  The most important thing is that your savings rate (ideally 20%+) stays protected.
                </span>
              </div>
            </div>
          )}

          {/* Spending Insights */}
          <SpendingInsights
            transactions={filteredTxns}
            allTransactions={transactions}
            income={income}
            totalBudgeted={totalBudgeted}
            period={period}
          />

          {/* Month in Review */}
          <MonthInReview
            data={mirData}
            monthLabel={now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          />
        </>
      )}
    </div>
  );
}
